/**
 * TSS Video Coaching Platform - API Client
 *
 * This module provides a typed API client for all backend communication.
 *
 * Architecture:
 * - All requests go through apiRequest() which handles auth token injection
 * - Type definitions mirror backend Pydantic models
 * - S3 uploads use XMLHttpRequest for progress tracking (fetch doesn't support it)
 *
 * Usage:
 *   import { listVideos, uploadToS3, getProfile } from './api';
 *   const { videos } = await listVideos();
 *
 * Error Handling:
 * - Throws Error with message from backend error.message field
 * - 'Not authenticated' thrown if no valid token available
 */

import { getConfig } from '../config';
import { getAuthToken } from '../auth';

// =============================================================================
// TYPE DEFINITIONS - Mirror backend models for type safety
// =============================================================================
export interface User {
  user_id: string;
  email: string;
  given_name: string;
  family_name: string;
  role: string;
  platform_role?: string; // USER, SUPPORT, SUPER_ADMIN
  // Profile fields
  profile_picture_url?: string | null;
  bio?: string | null;
  phone_number?: string | null;
  // Athlete-specific
  position?: string | null;
  jersey_number?: string | null;
  date_of_birth?: string | null;
  height?: string | null;
  weight?: string | null;
  dominant_hand?: string | null; // "left", "right", "ambidextrous"
  // Learning & motivation (for AI personalization)
  learning_style?: string | null; // "visual", "auditory", "kinesthetic", "reading"
  motivation_type?: string | null; // "competition", "improvement", "team", "fun"
  preferred_feedback_style?: string | null; // "direct", "encouraging", "detailed"
  // Training preferences (for AI program design)
  preferred_workout_time?: string | null; // "morning", "afternoon", "evening", "flexible"
  preferred_workout_duration?: string | null; // "short", "medium", "long"
  equipment_available?: string[]; // ["basketball", "hoop", "cones", "resistance_bands"]
  training_environment?: string | null; // "home", "gym", "outdoor", "school"
  // Goals & aspirations (for AI long-term planning)
  athletic_goals?: string[]; // ["improve 3pt shot", "increase vertical"]
  target_level?: string | null; // "recreational", "high_school", "college", "professional"
  graduation_year?: number | null; // For recruiting context
  // Health & wellness (for AI to consider limitations)
  injury_history?: Array<{ injury: string; date: string; recovered: boolean }>;
  current_limitations?: string[]; // Active limitations to avoid in programs
  dietary_restrictions?: string[]; // ["vegetarian", "gluten-free", "nut allergy"]
  tracks_nutrition?: boolean; // Whether user wants nutrition tips
  // Coach-specific
  certifications?: string[];
  years_experience?: number | null;
  specialties?: string[];
  // Preferences
  timezone?: string | null;
  // Notification preferences
  notify_on_comment?: boolean;
  notify_on_feedback?: boolean;
  notify_on_assignment?: boolean;
  // Subscription
  subscription_status?: string;
  subscription_tier?: string;
  trial_ends_at?: string | null;
  subscription_expires_at?: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface Video {
  video_id: string;
  org_id: string;
  user_id?: string;
  uploader_name?: string | null; // Name of user who uploaded (for coach view)
  title: string;
  description: string | null;
  tags?: string[];
  status: string;
  uploader_role?: string | null; // Role at upload time (OWNER, COACH, ATHLETE)
  file_size_bytes: number;
  duration_seconds?: number | null; // Captured from MediaConvert on transcode complete; absent for legacy / failed transcodes
  is_deleted?: boolean; // Soft delete flag
  deleted_at?: string | null; // When the video was soft deleted
  created_at: string;
  updated_at: string;
}

export interface UploadUrlResponse {
  video_id: string;
  upload_url: string;
  fields: Record<string, string>;
}

export interface Organization {
  org_id: string;
  name: string;
  role: string;
  joined_at?: string;
  owner_id?: string;
  created_at?: string;
  invite_code?: string; // Included for OWNER/COACH roles from listOrgs
}

export interface Comment {
  comment_id: string;
  user_id: string;
  author_name: string;
  content: string;
  timestamp_seconds: number | null;
  parent_comment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Program {
  program_id: string;
  org_id: string;
  title: string;
  description: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  is_template: boolean;
  created_by: string;
  created_at: string;
  is_shared?: boolean;
  shared_by?: string;
}

export interface Drill {
  name: string;
  description: string;
  duration_seconds?: number;
  reps?: number;
  sets?: number;
  rest_seconds?: number;
  tips?: string[];
  skill_tags?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  requiresProof?: boolean;
  animation_prompt?: string;
}

export interface GeneratedImage {
  drill_name: string;
  s3_key: string;
  bucket: string;
  created_at: string;
}

export interface GeneratedVideo {
  drill_name: string;
  status: 'pending' | 'complete';
  s3_key?: string;
  created_at: string;
}

// Drill object format (can be stored as object or JSON string)
export interface DrillData {
  id?: string;
  name: string;
  description?: string;
  category?: string;
  // Duration and timing
  duration?: number; // Legacy: duration in minutes
  duration_seconds?: number; // Preferred: duration in seconds
  reps?: number;
  sets?: number;
  rest_seconds?: number;
  // Metadata
  skill_tags?: string[];
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  // Video requirements
  requires_proof?: boolean; // Preferred format
  requiresProof?: boolean; // Legacy format
  instructionVideoId?: string;
}

// Warmup/cooldown exercise structure
export interface WarmupExercise {
  name: string;
  reps?: number;
  duration_seconds?: number;
  description?: string;
}

export interface Module {
  module_id: string;
  program_id?: string;
  order: number;
  title: string;
  description: string | null;
  content: string | null;
  video_ids: string[];
  drills: (string | DrillData)[]; // Can be JSON strings or drill objects
  warmup_exercises?: WarmupExercise[]; // Specific warmup exercises like "10 pushups"
  cooldown_exercises?: WarmupExercise[]; // Specific cooldown exercises
  tags?: string[];
  duration_estimate: string | null;
  duration_minutes?: number;
  week?: number;
  session?: number;
  ai_generated?: boolean;
  generated_images?: GeneratedImage[];
  generated_videos?: GeneratedVideo[];
}

export interface ProgramAssignment {
  assignment_id: string;
  program_id: string;
  athlete_id: string;
  current_module_id: string | null;
  completed_module_ids: string[];
  progress_percentage: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

// Team types - for organizing athletes into groups
export interface Team {
  team_id: string;
  org_id: string;
  name: string;
  description?: string | null;
  coach_id: string;
  coach_name?: string;
  color?: string | null;
  grade_level?: string | null; // For age-appropriate AI baselines
  age_range?: string | null; // Human-readable age range
  skill_level?: string | null; // beginner, intermediate, advanced
  member_count?: number;
  created_at: string;
}

export interface TeamMember {
  team_id: string;
  user_id: string;
  org_id: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  profile_picture_url?: string | null;
  position?: string | null;
  jersey_number?: string | null;
  joined_at: string;
}

// Challenge types - team competitions and leaderboards
export type ChallengeStatus = 'UPCOMING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
export type ChallengeType = 'WEEKLY' | 'MONTHLY' | 'CUSTOM';
export type ChallengeMetric =
  | 'DRILL_COMPLETIONS'
  | 'VIDEO_UPLOADS'
  | 'PROOF_SUBMISSIONS'
  | 'STREAK_DAYS'
  | 'PROGRAM_PROGRESS'
  | 'POINTS';
export type ChallengeScope = 'TEAM_VS_TEAM' | 'INDIVIDUAL' | 'CROSS_ORG';

export interface Challenge {
  challenge_id: string;
  org_id: string;
  title: string;
  description?: string | null;
  challenge_type: ChallengeType;
  metric: ChallengeMetric;
  scope: ChallengeScope;
  start_date: string;
  end_date: string;
  status: ChallengeStatus;
  participant_ids: string[];
  target_value?: number | null;
  prize_description?: string | null;
  // Raffle/incentive settings
  is_raffle?: boolean;
  raffle_min_entries?: number;
  raffle_entries_per_completion?: number;
  raffle_drawn_at?: string | null;
  raffle_winner_id?: string | null;
  raffle_winner_name?: string | null;
  winner_id?: string | null;
  final_rankings?: ChallengeRanking[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ChallengeRanking {
  participant_id: string;
  participant_name: string;
  final_value: number;
  rank: number;
}

export interface ChallengeProgress {
  participant_id: string;
  participant_name: string;
  current_value: number;
  rank: number;
  current_streak: number;
  longest_streak: number;
  breakdown?: Record<string, unknown>;
  updated_at: string;
}

export interface ChallengeActivity {
  activity_id: string;
  participant_id: string;
  user_id: string;
  user_name: string;
  activity_type: string;
  points_earned: number;
  description: string;
  created_at: string;
}

// Badge types - achievements and rewards
export type BadgeType =
  | 'CHALLENGE_WINNER'
  | 'CHALLENGE_RUNNER_UP'
  | 'STREAK_MASTER'
  | 'PROOF_STAR'
  | 'TEAM_PLAYER'
  | 'FIRST_CHALLENGE'
  | 'PERFECT_WEEK';

export interface UserBadge {
  badge_id: string;
  badge_type: BadgeType;
  name: string;
  description: string;
  icon: string;
  color: string;
  tier: number;
  points: number;
  org_id?: string | null;
  challenge_id?: string | null;
  challenge_title?: string | null;
  earned_at: string;
}

// Performance Grade types - athlete progress tracking
export type PerformanceGrade = 'ELITE' | 'GREAT' | 'GOOD' | 'DEVELOPING' | 'POOR';

export interface GradeInfo {
  label: string;
  color: string;
  emoji: string;
  description: string;
}

export const GRADE_CONFIG: Record<PerformanceGrade, GradeInfo> = {
  ELITE: {
    label: 'Elite',
    color: '#8B5CF6',
    emoji: '🏆',
    description: 'Exceptional dedication! Top performer.',
  },
  GREAT: {
    label: 'Great',
    color: '#10B981',
    emoji: '⭐',
    description: 'Strong commitment to training.',
  },
  GOOD: {
    label: 'Good',
    color: '#3B82F6',
    emoji: '👍',
    description: 'Solid progress, keep it up!',
  },
  DEVELOPING: {
    label: 'Developing',
    color: '#F59E0B',
    emoji: '📈',
    description: 'Room for improvement. Stay consistent!',
  },
  POOR: {
    label: 'Needs Work',
    color: '#EF4444',
    emoji: '⚠️',
    description: 'Falling behind. Time to step up!',
  },
};

// Drill Flag types - coach feedback on drill submissions
export type DrillFlag =
  | 'NONE'
  | 'EXCELLENT'
  | 'GOOD_PROGRESS'
  | 'NEEDS_CORRECTION'
  | 'REVIEW_LATER'
  | 'AI_FLAGGED';

export const DRILL_FLAG_CONFIG: Record<DrillFlag, { label: string; color: string; icon: string }> =
  {
    NONE: { label: 'No Flag', color: '#94a3b8', icon: 'minus' },
    EXCELLENT: { label: 'Excellent', color: '#8B5CF6', icon: 'star' },
    GOOD_PROGRESS: { label: 'Good Progress', color: '#10B981', icon: 'trending-up' },
    NEEDS_CORRECTION: { label: 'Needs Work', color: '#EF4444', icon: 'alert-circle' },
    REVIEW_LATER: { label: 'Review Later', color: '#F59E0B', icon: 'clock' },
    AI_FLAGGED: { label: 'AI Flagged', color: '#3B82F6', icon: 'zap' },
  };

// Athlete Progress types - for weekly/monthly reports
export interface AthleteProgress {
  athlete_id: string;
  athlete_name: string;
  monthly: ProgressPeriod;
  weekly: ProgressPeriod;
  history: ProgressSnapshot[];
}

export interface ProgressPeriod {
  drills_completed: number;
  drills_assigned: number;
  completion_rate: number;
  completion_percent: number;
  grade: PerformanceGrade;
  grade_label: string;
  grade_color: string;
  grade_emoji?: string;
  grade_description?: string;
  videos_uploaded: number;
  streak_days: number;
  active_days: number;
  excellent_flags?: number;
  needs_work_flags?: number;
}

export interface ProgressSnapshot {
  period_type: 'WEEKLY' | 'MONTHLY';
  period_start: string;
  period_end: string;
  completion_rate: number;
  grade: PerformanceGrade;
  grade_label: string;
  grade_color: string;
  drills_completed: number;
  drills_assigned: number;
}

// Athlete Skill Assessment types - coach evaluations of athlete skills
export interface SkillRatings {
  shooting?: number;
  ball_handling?: number;
  passing?: number;
  defense?: number;
  rebounding?: number;
  footwork?: number;
  conditioning?: number;
  court_vision?: number;
  decision_making?: number;
  teamwork?: number;
  speed?: number;
  agility?: number;
  strength?: number;
  vertical?: number;
  free_throws?: number;
  [key: string]: number | undefined; // Allow other custom skills
}

export interface AthleteSkillAssessment {
  assessment_id: string;
  athlete_id: string;
  athlete_name?: string;
  coach_id: string;
  coach_name?: string;
  org_id: string;
  skill_ratings: SkillRatings;
  overall_rating?: number | null; // 1-10 scale
  strengths: string[];
  areas_to_improve: string[];
  notes?: string | null;
  assessment_type:
    | 'general'
    | 'pre_season'
    | 'mid_season'
    | 'post_season'
    | 'post_game'
    | 'tryout'
    | 'weekly'
    | 'monthly';
  created_at: string;
  updated_at: string;
}

export interface CreateAssessmentRequest {
  skill_ratings?: SkillRatings;
  overall_rating?: number;
  strengths?: string[];
  areas_to_improve?: string[];
  notes?: string;
  assessment_type?: string;
}

export interface AssessmentListResponse {
  athlete_id: string;
  athlete_name: string;
  assessments: AthleteSkillAssessment[];
  count: number;
}

// AI Preferences types - organization-level AI customization
export interface WorkoutInterval {
  name: string; // e.g., "quick", "basic", "standard", "complex"
  min_seconds: number;
  max_seconds: number;
  description?: string;
}

export interface DrillTemplate {
  name: string;
  description: string;
  duration_seconds: number;
  reps?: number | null;
  sets: number;
  rest_seconds: number;
  skill_tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  equipment_needed: string[];
  tips: string[];
}

export interface OrgAIPreferences {
  org_id: string;
  // Coaching style
  coaching_style?: string | null; // e.g., "intense", "technical", "fun", "competitive"
  coaching_notes?: string | null;
  sport_focus: string;
  // Duration preferences (seconds)
  default_warmup_duration: number;
  default_cooldown_duration: number;
  min_drill_duration: number;
  max_drill_duration: number;
  default_rest_between_sets: number;
  default_rest_between_drills: number;
  // Workout intervals
  workout_intervals: WorkoutInterval[];
  // Tags
  custom_skill_tags: string[];
  default_skill_tags: string[];
  // Equipment
  available_equipment: string[];
  // Drill templates
  drill_templates: DrillTemplate[];
  // Difficulty
  preferred_difficulty: 'beginner' | 'intermediate' | 'advanced';
  scale_difficulty_by_week: boolean;
  // Grade level / age targeting (for AI cohesion across all touchpoints)
  target_grade_level?: string | null; // elementary, middle_school, high_school, college_pro
  target_age_range?: string | null; // Human-readable, e.g., "11-13 years old"
  use_grade_baselines: boolean;
  // Focus areas for AI prioritization (highlights, play detection, video analysis)
  focus_areas: string[];
  // Session structure
  drills_per_session_min: number;
  drills_per_session_max: number;
  always_include_warmup: boolean;
  always_include_cooldown: boolean;
  proof_requirement_percentage: number;
  // AI behavior
  ai_creativity_level: 'conservative' | 'balanced' | 'creative';
  use_only_custom_drills: boolean;
  include_tips_in_drills: boolean;
  include_equipment_in_drills: boolean;
  // Timestamps
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  updated_by?: string | null;
}

// =============================================================================
// API CLIENT CORE
// =============================================================================

/**
 * In-memory response cache for frequently accessed data
 * Reduces redundant API calls and improves perceived performance
 */
const responseCache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_DURATION = 30 * 1000; // 30 seconds for in-memory cache

/**
 * Request deduplication - prevents duplicate in-flight requests
 */
const inflightRequests = new Map<string, Promise<unknown>>();

/**
 * Clear cached data for a specific path pattern (for cache invalidation)
 */
export function invalidateCache(pathPattern: string): void {
  for (const key of responseCache.keys()) {
    if (key.includes(pathPattern)) {
      responseCache.delete(key);
    }
  }
}

/**
 * Generic API request handler with authentication
 * Automatically injects Bearer token from Cognito session
 *
 * Features:
 * - Smart caching: GET requests use browser cache, mutations bypass it
 * - Request deduplication: identical in-flight requests are consolidated
 * - In-memory cache: 30-second TTL for frequently accessed endpoints
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  useCache: boolean = true // Allow caching for GET requests
): Promise<T> {
  const config = getConfig();
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const method = options.method?.toUpperCase() || 'GET';
  const isReadOperation = method === 'GET';
  const cacheKey = `${method}:${path}`;

  // For GET requests, check in-memory cache first
  if (isReadOperation && useCache) {
    const cached = responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data as T;
    }

    // Check for duplicate in-flight request
    const inflight = inflightRequests.get(cacheKey);
    if (inflight) {
      return inflight as Promise<T>;
    }
  }

  // Determine cache strategy:
  // - GET requests: use 'default' to respect server Cache-Control headers
  // - Mutations (POST/PUT/DELETE): use 'no-store' to always hit the server
  const cacheStrategy: RequestCache = isReadOperation ? 'default' : 'no-store';

  const requestPromise = (async () => {
    let response: Response;
    try {
      response = await fetch(`${config.api.endpoint}${path}`, {
        ...options,
        cache: cacheStrategy,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });
    } catch (err) {
      console.error('Network error:', err);
      throw new Error('Network error - please check your connection');
    }

    let data;
    try {
      data = await response.json();
    } catch {
      // Response body is not valid JSON
      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }
      throw new Error('Invalid response from server');
    }

    if (!response.ok) {
      throw new Error(data.error?.message || data.message || `Request failed (${response.status})`);
    }

    return data;
  })();

  // Track in-flight request for deduplication
  if (isReadOperation && useCache) {
    inflightRequests.set(cacheKey, requestPromise);
  }

  try {
    const result = await requestPromise;

    // Cache successful GET responses
    if (isReadOperation && useCache) {
      responseCache.set(cacheKey, { data: result, timestamp: Date.now() });
    }

    return result as T;
  } finally {
    // Clean up in-flight tracking
    inflightRequests.delete(cacheKey);
  }
}

// =============================================================================
// USER PROFILE API
// =============================================================================

export interface ProfileResponse {
  user: User;
  is_new_user: boolean;
  needs_onboarding: boolean;
}

export async function getProfile(): Promise<ProfileResponse> {
  return apiRequest('/api/v1/user/profile');
}

export async function updateProfile(
  data: Partial<
    Omit<
      User,
      | 'user_id'
      | 'email'
      | 'role'
      | 'platform_role'
      | 'created_at'
      | 'updated_at'
      | 'subscription_status'
      | 'subscription_tier'
      | 'trial_ends_at'
      | 'subscription_expires_at'
    >
  >
): Promise<{ user: User }> {
  return apiRequest('/api/v1/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getUserBadges(athleteId?: string): Promise<{ badges: UserBadge[] }> {
  const params = athleteId ? `?athlete_id=${athleteId}` : '';
  return apiRequest(`/api/v1/user/badges${params}`);
}

// =============================================================================
// PROFILE PICTURE UPLOAD
// =============================================================================

export interface ProfilePictureUploadUrlResponse {
  upload_url: string;
  fields: Record<string, string>;
  public_url: string;
  s3_key: string;
}

/**
 * Request a presigned POST for an avatar upload.
 *
 * The Lambda derives the S3 key from the authenticated user_id, so the caller
 * cannot influence what user the image lands under.
 */
export async function getProfilePictureUploadUrl(
  filename: string,
  file_size: number
): Promise<ProfilePictureUploadUrlResponse> {
  return apiRequest('/api/v1/user/profile-picture/upload-url', {
    method: 'POST',
    body: JSON.stringify({ filename, file_size }),
  });
}

/**
 * Full profile-picture upload flow: presign → PUT to S3 → PATCH profile.
 *
 * Returns the updated User. `onProgress` reports the S3 PUT progress (0-100).
 *
 * The user record stores the S3 *key* (e.g. ``avatars/<user>/<uuid>.jpg``),
 * not a fully-formed URL. The user_profile Lambda signs that key into a
 * short-lived GET URL on every read — VideoBucket is private so a raw
 * https URL would 403. The user.profile_picture_url returned here is
 * already-signed, so the caller can render it directly.
 */
export async function uploadProfilePicture(
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ user: User }> {
  const presigned = await getProfilePictureUploadUrl(file.name, file.size);
  await uploadToS3(presigned.upload_url, presigned.fields, file, onProgress);
  // Store the key, not the URL. user_profile signs it on read.
  const { user } = await updateProfile({ profile_picture_url: presigned.s3_key });
  return { user };
}

// =============================================================================
// VIDEO API - Core video CRUD and upload operations
// =============================================================================

/**
 * List videos for the current user.
 *
 * @param orgId - Organization ID (required for guardians to see linked athlete videos)
 * @param includeDeleted - If true, includes soft-deleted videos (for recovery UI)
 * @param athleteId - For guardian proxy mode: filter to only this athlete's videos
 */
export async function listVideos(
  orgId?: string,
  includeDeleted = false,
  athleteId?: string
): Promise<{ videos: Video[] }> {
  const params: string[] = [];
  if (orgId) params.push(`org_id=${orgId}`);
  if (includeDeleted) params.push('include_deleted=true');
  if (athleteId) params.push(`athlete_id=${athleteId}`);

  const url = params.length > 0 ? `/api/v1/videos?${params.join('&')}` : '/api/v1/videos';
  return apiRequest(url);
}

/**
 * List videos from athletes the coach has a relationship with.
 * Only works for COACH and MODERATOR roles.
 */
export async function listCoachedAthleteVideos(orgId: string): Promise<{ videos: Video[] }> {
  return apiRequest(`/api/v1/videos?org_id=${orgId}&scope=coached`);
}

/**
 * Get video details with playback URL.
 *
 * IMPORTANT: Playback URLs expire after 1 hour (S3 presigned URLs) or are permanent (CloudFront CDN).
 * For long viewing sessions, refresh the URL by calling this function again if playback fails.
 *
 * The frontend should handle 403 errors on video playback by:
 * 1. Calling getVideo() again to get a fresh playback_url
 * 2. Retrying playback with the new URL
 */
export async function getVideo(
  videoId: string
): Promise<{ video: Video; playback_url: string | null }> {
  return apiRequest(`/api/v1/videos/${videoId}`);
}

export async function requestUploadUrl(data: {
  org_id: string;
  filename: string;
  file_size: number;
  title: string;
  description?: string;
  skip_transcode?: boolean;
  athlete_id?: string; // For guardian proxy mode
}): Promise<UploadUrlResponse> {
  return apiRequest('/api/v1/videos/upload-url', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeUpload(videoId: string): Promise<void> {
  return apiRequest('/api/v1/videos/complete', {
    method: 'POST',
    body: JSON.stringify({ video_id: videoId }),
  });
}

export async function updateVideo(
  videoId: string,
  data: {
    title?: string;
    description?: string;
    tags?: string[];
  }
): Promise<{ video: Video }> {
  return apiRequest(`/api/v1/videos/${videoId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a video (soft delete by default).
 *
 * @param videoId - The video ID to delete
 * @param permanent - If true, permanently deletes video and S3 file
 */
export async function deleteVideo(
  videoId: string,
  permanent = false
): Promise<{ message: string }> {
  const url = permanent ? `/api/v1/videos/${videoId}?permanent=true` : `/api/v1/videos/${videoId}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
}

/**
 * Restore a soft-deleted video.
 */
export async function restoreVideo(videoId: string): Promise<{ message: string; video: Video }> {
  return apiRequest(`/api/v1/videos/${videoId}`, {
    method: 'PATCH',
  });
}

/**
 * Reprocess (retranscode) a video.
 *
 * Triggers a new MediaConvert transcoding job for the video.
 * Requires the raw video file to still exist in S3.
 *
 * Use cases:
 * - Fix failed transcoding jobs
 * - Apply new encoding settings
 * - Re-generate output after configuration changes
 *
 * @param videoId - The video ID to reprocess
 */
export async function reprocessVideo(videoId: string): Promise<{
  message: string;
  job_id: string;
  video_id: string;
  status: string;
}> {
  return apiRequest(`/api/v1/videos/${videoId}/reprocess`, {
    method: 'POST',
  });
}

// Upload file to S3 using presigned URL
export async function uploadToS3(
  uploadUrl: string,
  fields: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();

    // Add all presigned fields
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add file last
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('POST', uploadUrl);
    xhr.send(formData);
  });
}

// =============================================================================
// ORGANIZATION API - Multi-tenancy management
// =============================================================================

export async function listOrgs(): Promise<{ orgs: Organization[] }> {
  return apiRequest('/api/v1/orgs');
}

export async function createOrg(name: string): Promise<{ org: Organization; invite_code: string }> {
  return apiRequest('/api/v1/orgs', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function joinOrg(
  inviteCode: string
): Promise<{ org: Organization; membership: { role: string } }> {
  return apiRequest('/api/v1/orgs/join', {
    method: 'POST',
    body: JSON.stringify({ invite_code: inviteCode }),
  });
}

export async function getOrg(
  orgId: string
): Promise<{ org: Organization; membership: { role: string }; invite_code?: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}`);
}

export async function listOrgVideos(orgId: string): Promise<{ videos: Video[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/videos`);
}

// =============================================================================
// AI PREFERENCES API - Organization AI customization
// =============================================================================

export async function getAIPreferences(orgId: string): Promise<{ preferences: OrgAIPreferences }> {
  return apiRequest(`/api/v1/orgs/${orgId}/ai-preferences`);
}

export async function updateAIPreferences(
  orgId: string,
  preferences: Partial<OrgAIPreferences>
): Promise<{ preferences: OrgAIPreferences; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/ai-preferences`, {
    method: 'PUT',
    body: JSON.stringify(preferences),
  });
}

// =============================================================================
// EMAIL BRANDING API - Organization email customization
// =============================================================================
export interface EmailSettings {
  email_logo_url: string;
  email_primary_color: string;
  email_header_text: string;
  email_footer_text: string;
  email_reply_to: string;
}

export async function getEmailSettings(orgId: string): Promise<EmailSettings> {
  return apiRequest(`/api/v1/orgs/${orgId}/email-settings`);
}

export async function updateEmailSettings(
  orgId: string,
  settings: Partial<EmailSettings>
): Promise<EmailSettings> {
  const response = await apiRequest<{ settings: EmailSettings }>(
    `/api/v1/orgs/${orgId}/email-settings`,
    {
      method: 'PUT',
      body: JSON.stringify(settings),
    }
  );
  return response.settings;
}

// =============================================================================
// MEMBERS API - Organization membership management
// =============================================================================
export interface Member {
  user_id: string;
  email: string | null;
  given_name: string | null;
  family_name: string | null;
  profile_picture_url?: string | null;
  bio?: string | null;
  phone_number?: string | null;
  position?: string | null;
  jersey_number?: string | null;
  role: 'OWNER' | 'COACH' | 'MODERATOR' | 'ATHLETE' | 'GUARDIAN';
  joined_at: string;
}

// Members API
export async function listMembers(orgId: string): Promise<{ members: Member[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/members`);
}

export async function updateMemberRole(
  orgId: string,
  memberId: string,
  role: 'COACH' | 'MODERATOR' | 'ATHLETE'
): Promise<{ message: string; member: { user_id: string; role: string } }> {
  return apiRequest(`/api/v1/orgs/${orgId}/members/${memberId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function removeMember(orgId: string, memberId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// TEAMS API - Team management within organizations
// =============================================================================

export async function listTeams(orgId: string, athleteId?: string): Promise<{ teams: Team[] }> {
  const params = athleteId ? `?athlete_id=${athleteId}` : '';
  return apiRequest(`/api/v1/orgs/${orgId}/teams${params}`);
}

export async function getTeam(
  orgId: string,
  teamId: string
): Promise<{ team: Team; members: TeamMember[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}`);
}

// =============================================================================
// ATHLETE METRICS (growth tracking) — SKOOL-12
// =============================================================================

export interface AthleteMetric {
  user_id: string;
  metric_type: 'height' | 'weight';
  value: string;
  unit: string | null;
  recorded_at: string;
  recorded_by_user_id: string | null;
}

/** Record a new physical measurement for the authenticated athlete. */
export async function recordAthleteMetric(data: {
  metric_type: 'height' | 'weight';
  value: string;
  unit?: string;
}): Promise<{ metric: AthleteMetric }> {
  return apiRequest('/api/v1/athlete/metrics', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * List the athlete's metric history (newest first). v1 only allows callers
 * to read their own history; coach/parent visibility is a follow-up.
 */
export async function listAthleteMetricHistory(
  athleteId: string,
  options: { metric?: 'height' | 'weight'; limit?: number } = {}
): Promise<{ metrics: AthleteMetric[]; count: number; metric_filter: string | null }> {
  const params = new URLSearchParams();
  if (options.metric) params.set('metric', options.metric);
  if (options.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const url = `/api/v1/athlete/${encodeURIComponent(athleteId)}/metrics/history${qs ? `?${qs}` : ''}`;
  return apiRequest(url);
}

// =============================================================================
// LEADERBOARD API
// =============================================================================

export interface LeaderboardEntry {
  participant_id: string;
  participant_name: string;
  profile_picture_url: string | null;
  position: string | null;
  current_value: number | null;
  rank: number | null;
  last_assessed_at: string | null;
}

export interface TeamLeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  metric: 'overall_rating';
  team_id: string;
  scope: 'team';
}

/**
 * Rank athletes within a team by their latest skill-assessment overall_rating.
 * Athletes without an assessment appear at the bottom with `current_value=null`
 * and `rank=null`.
 */
export async function getTeamLeaderboard(
  orgId: string,
  teamId: string
): Promise<TeamLeaderboardResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}/leaderboard`);
}

export async function createTeam(
  orgId: string,
  data: {
    name: string;
    description?: string;
    color?: string;
    grade_level?: string;
    age_range?: string;
    skill_level?: string;
  }
): Promise<{ team: Team; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTeam(
  orgId: string,
  teamId: string,
  data: {
    name?: string;
    description?: string | null;
    color?: string;
    coach_id?: string;
    grade_level?: string | null;
    age_range?: string | null;
    skill_level?: string | null;
  }
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTeam(orgId: string, teamId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}`, {
    method: 'DELETE',
  });
}

export async function listTeamMembers(
  orgId: string,
  teamId: string
): Promise<{ members: TeamMember[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}/members`);
}

export async function addTeamMember(
  orgId: string,
  teamId: string,
  data: { user_id: string; position?: string; jersey_number?: string }
): Promise<{ message: string; team_member: TeamMember }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeTeamMember(
  orgId: string,
  teamId: string,
  userId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/teams/${teamId}/members/${userId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// CHALLENGES API - Team competitions and leaderboards
// =============================================================================

export async function listChallenges(
  orgId: string,
  status?: ChallengeStatus
): Promise<{ challenges: Challenge[] }> {
  const url = status
    ? `/api/v1/orgs/${orgId}/challenges?status=${status}`
    : `/api/v1/orgs/${orgId}/challenges`;
  return apiRequest(url);
}

export async function getChallenge(
  orgId: string,
  challengeId: string
): Promise<{ challenge: Challenge; leaderboard: ChallengeProgress[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}`);
}

export async function createChallenge(
  orgId: string,
  data: {
    title: string;
    description?: string;
    challenge_type?: ChallengeType;
    metric?: ChallengeMetric;
    scope?: ChallengeScope;
    start_date: string;
    end_date: string;
    participant_ids?: string[];
    target_value?: number;
    prize_description?: string;
    // Raffle settings
    is_raffle?: boolean;
    raffle_min_entries?: number;
    raffle_entries_per_completion?: number;
  }
): Promise<{ challenge: Challenge }> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChallenge(
  orgId: string,
  challengeId: string,
  data: Partial<{
    title: string;
    description: string;
    status: ChallengeStatus;
    participant_ids: string[];
    prize_description: string;
  }>
): Promise<{ challenge: Challenge }> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteChallenge(
  orgId: string,
  challengeId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}`, {
    method: 'DELETE',
  });
}

export async function getChallengeLeaderboard(
  orgId: string,
  challengeId: string
): Promise<{ leaderboard: ChallengeProgress[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}/leaderboard`);
}

export async function getChallengeActivities(
  orgId: string,
  challengeId: string,
  limit?: number
): Promise<{ activities: ChallengeActivity[] }> {
  const url = limit
    ? `/api/v1/orgs/${orgId}/challenges/${challengeId}/activities?limit=${limit}`
    : `/api/v1/orgs/${orgId}/challenges/${challengeId}/activities`;
  return apiRequest(url);
}

export async function recordChallengeActivity(
  orgId: string,
  challengeId: string,
  data: {
    participant_id: string;
    user_id?: string;
    activity_type: string;
    points?: number;
    description: string;
    reference_type?: string;
    reference_id?: string;
  }
): Promise<{
  activity: { activity_id: string; points_earned: number };
  progress: ChallengeProgress;
}> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}/record`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface RaffleDrawResult {
  message: string;
  winner: {
    participant_id: string;
    participant_name: string;
  };
  total_entries: number;
  participants_eligible: number;
  all_participants: Array<{
    participant_id: string;
    participant_name: string;
    completions: number;
    entries: number;
  }>;
  prize: string | null;
}

/**
 * Draw a random winner for a raffle challenge.
 * Only coaches/owners can draw raffles.
 */
export async function drawRaffle(orgId: string, challengeId: string): Promise<RaffleDrawResult> {
  return apiRequest(`/api/v1/orgs/${orgId}/challenges/${challengeId}/draw-raffle`, {
    method: 'POST',
  });
}

// =============================================================================
// EMAIL INVITES API - One-time invite links
// =============================================================================

export interface EmailInvite {
  email: string;
  role: 'COACH' | 'MODERATOR' | 'ATHLETE';
  expires_at: string;
  created_at: string;
  used: boolean;
  is_valid: boolean;
}

export async function sendEmailInvite(
  orgId: string,
  email: string,
  role: 'COACH' | 'MODERATOR' | 'ATHLETE' = 'ATHLETE'
): Promise<{ invite: EmailInvite; message: string; invite_link?: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/email-invites`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function listEmailInvites(orgId: string): Promise<{ invites: EmailInvite[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/email-invites`);
}

export async function cancelEmailInvite(
  orgId: string,
  email: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/email-invites/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

export async function resendEmailInvite(
  orgId: string,
  email: string
): Promise<{ invite: EmailInvite; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/email-invites/${encodeURIComponent(email)}/resend`, {
    method: 'POST',
  });
}

export async function acceptEmailInvite(
  token: string
): Promise<{ org: Organization; message: string }> {
  return apiRequest('/api/v1/orgs/accept-invite', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function acceptFamilyInvite(
  orgId: string,
  token: string
): Promise<{ link: FamilyLink; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/links`, {
    method: 'POST',
    body: JSON.stringify({ invite_token: token }),
  });
}

// =============================================================================
// TEAM INVITE CODE API - Join org/team via QR code
// =============================================================================
export interface JoinViaTeamCodeResponse {
  org: Organization;
  team: {
    team_id: string;
    name: string;
    description?: string;
  };
  membership: {
    org_role: string;
    team_role: string;
  };
  onboarding?: {
    steps_completed: string[];
    is_complete: boolean;
  };
  trial?: {
    granted: boolean;
    tier: string;
    trial_days: number;
    trial_end: string;
  };
  message: string;
}

/**
 * Join an organization and team via team invite code (from QR code scan).
 * User must be authenticated. No email validation required.
 */
export async function joinViaTeamCode(teamCode: string): Promise<JoinViaTeamCodeResponse> {
  return apiRequest('/api/v1/orgs/join-team', {
    method: 'POST',
    body: JSON.stringify({ team_code: teamCode }),
  });
}

// Validated invite information from public endpoint
export interface ValidatedInvite {
  token: string; // The invite token (for passing to signup)
  email: string;
  role: string;
  org_name: string;
  org_id: string;
  invited_by_name: string;
  expires_at: string;
  invite_type?: 'EMAIL' | 'FAMILY'; // Type of invite
  athlete_name?: string; // For family invites - the athlete being linked
  link_id?: string; // For family invites - the link ID
}

export interface ValidateInviteResponse {
  valid: boolean;
  reason?: 'expired' | 'used' | 'not_found' | 'missing_token';
  invite?: ValidatedInvite;
}

/**
 * Validate an invite token (PUBLIC - no auth required).
 * Used to check if an invite is valid before showing signup form.
 * This allows invite-based signup even when general signup is disabled.
 */
export async function validateInvite(token: string): Promise<ValidateInviteResponse> {
  const config = getConfig();
  const url = `${config.api.endpoint}/api/v1/orgs/validate-invite?token=${encodeURIComponent(token)}`;
  console.log('[validateInvite] Fetching:', url);
  try {
    const response = await fetch(url);
    console.log('[validateInvite] Response status:', response.status);
    if (!response.ok) {
      console.log('[validateInvite] Response not OK, returning not_found');
      return { valid: false, reason: 'not_found' };
    }
    const data = await response.json();
    console.log('[validateInvite] Response data:', data);
    // Add the token to the invite data so it can be passed to signup
    if (data.valid && data.invite) {
      data.invite.token = token;
    }
    return data;
  } catch (err) {
    console.error('[validateInvite] Fetch error:', err);
    return { valid: false, reason: 'not_found' };
  }
}

// =============================================================================
// COMMENTS API - Video comments with threading
// =============================================================================
export interface ListCommentsResponse {
  comments: Comment[];
  next_cursor: string | null;
  total_count: number;
}

export async function listComments(
  videoId: string,
  options?: { limit?: number; cursor?: string; includeReplies?: boolean }
): Promise<ListCommentsResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.cursor) params.set('cursor', options.cursor);
  if (options?.includeReplies !== undefined) {
    params.set('include_replies', options.includeReplies.toString());
  }
  const queryString = params.toString();
  const url = `/api/v1/videos/${videoId}/comments${queryString ? `?${queryString}` : ''}`;
  return apiRequest(url);
}

export async function addComment(
  videoId: string,
  content: string,
  timestampSeconds?: number,
  parentCommentId?: string
): Promise<{ comment: Comment }> {
  return apiRequest(`/api/v1/videos/${videoId}/comments`, {
    method: 'POST',
    body: JSON.stringify({
      content,
      timestamp_seconds: timestampSeconds,
      parent_comment_id: parentCommentId,
    }),
  });
}

export async function updateComment(
  videoId: string,
  commentId: string,
  content: string
): Promise<{ comment: Comment }> {
  return apiRequest(`/api/v1/videos/${videoId}/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function deleteComment(
  videoId: string,
  commentId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/videos/${videoId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// ANNOTATIONS API - Video drawing/markup tools
// =============================================================================

export type AnnotationType = 'CIRCLE' | 'ARROW' | 'LINE' | 'FREEHAND' | 'RECTANGLE' | 'TEXT';

export interface AnnotationData {
  // Circle: x, y, radius (normalized 0-1)
  x?: number;
  y?: number;
  radius?: number;
  // Arrow/Line: x1, y1, x2, y2
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  // Rectangle: x, y, width, height
  width?: number;
  height?: number;
  // Freehand: points array
  points?: Array<{ x: number; y: number }>;
  // Text: text content
  text?: string;
}

export interface Annotation {
  annotation_id: string;
  video_id: string;
  user_id: string;
  timestamp_seconds: number;
  duration_seconds: number;
  annotation_type: AnnotationType;
  data: AnnotationData;
  color: string;
  stroke_width: number;
  created_at: string;
}

export async function listAnnotations(
  videoId: string,
  options?: { fromTime?: number; toTime?: number }
): Promise<{ annotations: Annotation[] }> {
  const params = new URLSearchParams();
  if (options?.fromTime !== undefined) params.set('from_time', options.fromTime.toString());
  if (options?.toTime !== undefined) params.set('to_time', options.toTime.toString());
  const queryString = params.toString();
  const url = `/api/v1/videos/${videoId}/annotations${queryString ? `?${queryString}` : ''}`;
  return apiRequest(url);
}

export async function createAnnotation(
  videoId: string,
  data: {
    timestamp_seconds: number;
    duration_seconds?: number;
    annotation_type: AnnotationType;
    data: AnnotationData;
    color?: string;
    stroke_width?: number;
  }
): Promise<{ annotation: Annotation }> {
  return apiRequest(`/api/v1/videos/${videoId}/annotations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteAnnotation(
  videoId: string,
  annotationId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/videos/${videoId}/annotations/${annotationId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// PROGRAMS API - Training program management
// =============================================================================

export async function listPrograms(orgId: string): Promise<{ programs: Program[] }> {
  return apiRequest(`/api/v1/programs?org_id=${orgId}`);
}

export async function getProgram(
  programId: string
): Promise<{ program: Program; modules: Module[] }> {
  return apiRequest(`/api/v1/programs/${programId}`);
}

export async function createProgram(data: {
  org_id: string;
  title: string;
  description?: string;
  is_template?: boolean;
}): Promise<{ program: Program }> {
  return apiRequest('/api/v1/programs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProgram(
  programId: string,
  data: {
    title?: string;
    description?: string;
    is_template?: boolean;
  }
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProgram(programId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}`, {
    method: 'DELETE',
  });
}

export async function publishProgram(programId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}/publish`, {
    method: 'POST',
  });
}

// =============================================================================
// MODULES API - Program module CRUD
// =============================================================================

export async function listModules(programId: string): Promise<{ modules: Module[] }> {
  return apiRequest(`/api/v1/programs/${programId}/modules`);
}

export async function createModule(
  programId: string,
  data: {
    title: string;
    order: number;
    description?: string;
    content?: string;
    video_ids?: string[];
    drills?: string[];
    warmup_exercises?: Array<{ name: string; reps?: number; duration_seconds?: number }>;
    cooldown_exercises?: Array<{ name: string; reps?: number; duration_seconds?: number }>;
    duration_estimate?: string;
  }
): Promise<{ module: Module }> {
  return apiRequest(`/api/v1/programs/${programId}/modules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateModule(
  programId: string,
  moduleId: string,
  data: {
    title?: string;
    description?: string;
    content?: string;
    video_ids?: string[];
    drills?: string[];
    warmup_exercises?: Array<{ name: string; reps?: number; duration_seconds?: number }>;
    cooldown_exercises?: Array<{ name: string; reps?: number; duration_seconds?: number }>;
    duration_estimate?: string;
  }
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}/modules/${moduleId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteModule(
  programId: string,
  moduleId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}/modules/${moduleId}`, {
    method: 'DELETE',
  });
}

// =============================================================================
// ASSIGNMENTS API - Program-to-athlete assignments
// =============================================================================

export async function listAssignments(
  programId: string
): Promise<{ assignments: ProgramAssignment[] }> {
  return apiRequest(`/api/v1/programs/${programId}/assignments`);
}

export async function assignProgram(
  programId: string,
  athleteId: string
): Promise<{ assignment: ProgramAssignment }> {
  return apiRequest(`/api/v1/programs/${programId}/assignments`, {
    method: 'POST',
    body: JSON.stringify({ athlete_id: athleteId }),
  });
}

// Current module with drills for athlete "Today" view
export interface CurrentModule {
  module_id: string;
  title: string;
  description?: string | null;
  order: number;
  drills: Array<{
    name: string;
    description?: string;
    duration?: string;
    reps?: number;
    sets?: number;
    requires_proof?: boolean;
  }>;
  warmup_exercises: Array<{ name: string; description?: string; duration?: string }>;
  cooldown_exercises: Array<{ name: string; description?: string; duration?: string }>;
  duration_minutes?: number | null;
  video_ids: string[];
}

// Athlete's assignment with current workout details
export interface MyAssignment {
  assignment_id: string;
  program_id: string;
  program_title: string;
  program_description?: string | null;
  progress_percentage: number;
  completed_module_ids: string[];
  total_modules: number;
  completed_modules: number;
  started_at: string | null;
  current_module: CurrentModule | null;
}

// Get athlete's own assignments with current module (powers "Today" view)
// Guardians can pass athleteId to get assignments for managed athletes
export async function getMyAssignments(
  orgId: string,
  athleteId?: string
): Promise<{
  assignments: MyAssignment[];
  streak_days: number;
  total_active: number;
}> {
  const params = new URLSearchParams({ org_id: orgId });
  if (athleteId) params.append('athlete_id', athleteId);
  return apiRequest(`/api/v1/programs/my-assignments?${params.toString()}`);
}

// =============================================================================
// DRILL SUBMISSIONS API - Athlete proof-of-work submissions
// =============================================================================

export interface DrillSubmission {
  submission_id: string;
  program_id?: string;
  module_id: string;
  drill_index: number;
  drill_name: string;
  user_id: string;
  user_name?: string;
  video_id: string;
  status: 'PENDING' | 'APPROVED' | 'NEEDS_WORK';
  // Coach flagging for drill quality
  flag?: DrillFlag;
  flag_reason?: string | null;
  flagged_by?: string | null;
  flagged_at?: string | null;
  coach_feedback: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export async function submitProof(
  programId: string,
  data: {
    module_id: string;
    drill_index: number;
    drill_name: string;
    video_id: string;
  }
): Promise<{ submission: DrillSubmission }> {
  return apiRequest(`/api/v1/programs/${programId}/submissions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listSubmissions(
  programId: string,
  options?: { module_id?: string; user_id?: string; athlete_id?: string }
): Promise<{ submissions: DrillSubmission[] }> {
  const params = new URLSearchParams();
  if (options?.module_id) params.append('module_id', options.module_id);
  // Support both user_id and athlete_id for consistency with workout logs API
  const userId = options?.athlete_id || options?.user_id;
  if (userId) params.append('user_id', userId);
  const query = params.toString();
  return apiRequest(`/api/v1/programs/${programId}/submissions${query ? `?${query}` : ''}`);
}

export async function reviewSubmission(
  programId: string,
  submissionId: string,
  data: { status: 'APPROVED' | 'NEEDS_WORK'; feedback?: string }
): Promise<{ message: string; status: string }> {
  return apiRequest(`/api/v1/programs/${programId}/submissions/${submissionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete a drill submission (athletes can delete their own, coaches can delete any)
export async function deleteSubmission(
  programId: string,
  submissionId: string
): Promise<{ message: string; submission_id: string; video_id: string }> {
  return apiRequest(`/api/v1/programs/${programId}/submissions/${submissionId}`, {
    method: 'DELETE',
  });
}

// Flag a drill submission (coach only)
export async function flagSubmission(
  programId: string,
  submissionId: string,
  data: {
    flag: DrillFlag;
    flag_reason?: string;
  }
): Promise<{ message: string; submission: DrillSubmission }> {
  return apiRequest(`/api/v1/programs/${programId}/submissions/${submissionId}/flag`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// ATHLETE PROGRESS API - Performance grades and progress tracking
// =============================================================================

/**
 * Get athlete progress with performance grades and history.
 * Accessible by: athlete (self), coaches, guardians
 */
export async function getAthleteProgress(
  orgId: string,
  athleteId: string
): Promise<AthleteProgress> {
  return apiRequest(`/api/v1/orgs/${orgId}/athletes/${athleteId}/progress`);
}

// =============================================================================
// PROGRAM SHARING API - Share programs between coaches
// =============================================================================

export interface ProgramShare {
  share_id: string;
  program_id?: string;
  shared_with: string;
  shared_by: string;
  can_edit: boolean;
  created_at: string;
}

export async function shareProgram(
  programId: string,
  userId: string
): Promise<{ share: ProgramShare }> {
  return apiRequest(`/api/v1/programs/${programId}/share`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export async function unshareProgram(
  programId: string,
  userId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/programs/${programId}/share/${userId}`, {
    method: 'DELETE',
  });
}

export async function listProgramShares(programId: string): Promise<{ shares: ProgramShare[] }> {
  return apiRequest(`/api/v1/programs/${programId}/share`);
}

// =============================================================================
// WORKOUT TRACKING API - Interactive athlete workout logging
// =============================================================================

export type DifficultyRating = 'TOO_EASY' | 'JUST_RIGHT' | 'CHALLENGING' | 'TOO_HARD';
export type ExerciseLogStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';
export type SkipReason =
  | 'NO_EQUIPMENT' // Don't have required equipment
  | 'INJURY' // Injured or feeling pain
  | 'TOO_HARD' // Exercise too difficult
  | 'NO_TIME' // Ran out of time
  | 'FATIGUE' // Too tired to continue
  | 'ALREADY_DID' // Already did this exercise today
  | 'NOT_UNDERSTOOD' // Didn't understand the exercise
  | 'OTHER'; // Other reason (see notes)

export interface WorkoutLog {
  log_id: string;
  user_id: string;
  program_id: string;
  module_id: string;
  drill_index: number;
  drill_name: string;
  // Prescribed values
  prescribed_reps: number | null;
  prescribed_sets: number | null;
  prescribed_weight: string | null;
  prescribed_duration: string | null;
  // Actual performance
  actual_reps: number | null;
  actual_sets: number | null;
  actual_weight: string | null;
  actual_duration: string | null;
  // Status and feedback
  status: ExerciseLogStatus;
  difficulty_rating: DifficultyRating | null;
  athlete_notes: string | null;
  skip_reason: SkipReason | null; // Why athlete skipped (if status is SKIPPED)
  // Proof submission tracking
  proof_submitted: boolean; // Whether proof video was submitted
  submission_id: string | null; // Reference to DrillSubmission if proof submitted
  // Timestamps
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at?: string;
}

export interface ModuleFeedback {
  feedback_id: string;
  user_id: string;
  program_id: string;
  module_id: string;
  overall_difficulty: DifficultyRating;
  energy_level: number | null; // 1-5 scale
  enjoyment_rating: number | null; // 1-5 scale
  what_went_well: string | null;
  what_was_hard: string | null;
  suggestions: string | null;
  total_duration_minutes: number | null;
  exercises_completed: number;
  exercises_skipped: number;
  created_at: string;
}

// Create a new workout log entry
export async function createWorkoutLog(
  programId: string,
  data: {
    module_id: string;
    drill_index: number;
    drill_name: string;
    prescribed_reps?: number;
    prescribed_sets?: number;
    prescribed_weight?: string;
    prescribed_duration?: string;
    athlete_id?: string; // For guardian proxy mode - log on behalf of managed athlete
  }
): Promise<{ workout_log: WorkoutLog }> {
  return apiRequest(`/api/v1/programs/${programId}/workout-logs`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Update a workout log with actual performance
export async function updateWorkoutLog(
  programId: string,
  logId: string,
  data: {
    actual_reps?: number;
    actual_sets?: number;
    actual_weight?: string;
    actual_duration?: string;
    status?: ExerciseLogStatus;
    difficulty_rating?: DifficultyRating;
    athlete_notes?: string;
    skip_reason?: SkipReason; // Why athlete skipped (required if status is SKIPPED)
    proof_submitted?: boolean; // Mark that proof was submitted
    submission_id?: string; // Reference to DrillSubmission
    athlete_id?: string; // For guardian proxy mode
  }
): Promise<{ workout_log: WorkoutLog }> {
  return apiRequest(`/api/v1/programs/${programId}/workout-logs/${logId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Get a specific workout log
export async function getWorkoutLog(
  programId: string,
  logId: string
): Promise<{ workout_log: WorkoutLog }> {
  return apiRequest(`/api/v1/programs/${programId}/workout-logs/${logId}`);
}

// List workout logs for a program
export async function listWorkoutLogs(
  programId: string,
  options?: { module_id?: string; athlete_id?: string }
): Promise<{ workout_logs: WorkoutLog[] }> {
  const params = new URLSearchParams();
  if (options?.module_id) params.append('module_id', options.module_id);
  if (options?.athlete_id) params.append('athlete_id', options.athlete_id);
  const query = params.toString();
  return apiRequest(`/api/v1/programs/${programId}/workout-logs${query ? `?${query}` : ''}`);
}

// Get previous workout values for pre-filling forms
export interface PreviousWorkoutValue {
  drill_index: number;
  drill_name: string;
  actual_reps: number | null;
  actual_sets: number | null;
  actual_weight: string | null;
  actual_duration: string | null;
  completed_at: string;
}

export async function getPreviousWorkoutValues(
  programId: string,
  moduleId: string
): Promise<{ previous_values: PreviousWorkoutValue[] }> {
  return apiRequest(`/api/v1/programs/${programId}/workout-logs/previous?module_id=${moduleId}`);
}

// Get workout history for a program
export interface WorkoutSession {
  date: string;
  module_id: string;
  drills: {
    drill_index: number;
    drill_name: string;
    actual_reps: number | null;
    actual_sets: number | null;
    actual_weight: string | null;
    actual_duration: string | null;
    status: ExerciseLogStatus;
    difficulty_rating: DifficultyRating | null;
  }[];
}

export async function getWorkoutHistory(
  programId: string,
  options?: { module_id?: string; athlete_id?: string; limit?: number }
): Promise<{ history: WorkoutSession[]; user_id: string }> {
  const params = new URLSearchParams();
  if (options?.module_id) params.append('module_id', options.module_id);
  if (options?.athlete_id) params.append('athlete_id', options.athlete_id);
  if (options?.limit) params.append('limit', options.limit.toString());
  const query = params.toString();
  return apiRequest(
    `/api/v1/programs/${programId}/workout-logs/history${query ? `?${query}` : ''}`
  );
}

// Submit module completion feedback
export async function submitModuleFeedback(
  programId: string,
  data: {
    module_id: string;
    overall_difficulty: DifficultyRating;
    energy_level?: number;
    enjoyment_rating?: number;
    what_went_well?: string;
    what_was_hard?: string;
    suggestions?: string;
    total_duration_minutes?: number;
    exercises_completed?: number;
    exercises_skipped?: number;
  }
): Promise<{ module_feedback: ModuleFeedback }> {
  return apiRequest(`/api/v1/programs/${programId}/module-feedback`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Get specific module feedback
export async function getModuleFeedback(
  programId: string,
  feedbackId: string
): Promise<{ module_feedback: ModuleFeedback }> {
  return apiRequest(`/api/v1/programs/${programId}/module-feedback/${feedbackId}`);
}

// List module feedback for a program
export async function listModuleFeedback(
  programId: string,
  options?: { module_id?: string; athlete_id?: string }
): Promise<{ module_feedbacks: ModuleFeedback[] }> {
  const params = new URLSearchParams();
  if (options?.module_id) params.append('module_id', options.module_id);
  if (options?.athlete_id) params.append('athlete_id', options.athlete_id);
  const query = params.toString();
  return apiRequest(`/api/v1/programs/${programId}/module-feedback${query ? `?${query}` : ''}`);
}

// =============================================================================
// AI ASSISTANT API - Bedrock Claude integration
// =============================================================================

export interface DashboardSummary {
  summary: string;
  highlights: string[];
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  return apiRequest('/api/v1/ai/dashboard-summary');
}

// Progress Insights - AI-powered progress analysis for athletes
export interface ProgressTrend {
  activity: string;
  count: number;
  trend: string;
}

export interface ProgressInsights {
  insights: string;
  focus_areas: string[];
  positive_trends: string[];
  trends: ProgressTrend[];
}

export async function getProgressInsights(): Promise<ProgressInsights> {
  return apiRequest('/api/v1/ai/progress-insights', {
    method: 'POST',
  });
}

// Progress Report - AI-powered reports for guardians
export interface AthleteReport {
  athlete_id: string;
  name: string;
  relationship?: string;
  video_count?: number;
  has_data?: boolean;
  ai_summary?: string;
  doing_well?: string;
  support_tip?: string;
}

export interface ProgressReport {
  report: string;
  athletes: AthleteReport[];
}

export async function getProgressReport(athleteId?: string): Promise<ProgressReport> {
  return apiRequest('/api/v1/ai/progress-report', {
    method: 'POST',
    body: athleteId ? JSON.stringify({ athlete_id: athleteId }) : undefined,
  });
}

// ============================================================================
// Video Analysis API - AI-powered basketball video analysis
// ============================================================================

export interface VideoSummary {
  // New simplified fields
  duration_seconds?: number;
  activity_type?: string;
  footage_type?: string;
  location?: string;
  // Legacy fields (for backward compatibility)
  duration_analyzed?: number | string;
  primary_activity?: string;
  skill_level_estimate?: string;
  overall_score?: number;
}

export interface MovementDetected {
  movement_type: string;
  timestamp_seconds: number;
  repetitions: number;
}

export interface TechniqueScores {
  form: number;
  consistency: number;
  power_efficiency: number;
}

export interface ImprovementArea {
  issue: string;
  impact: string;
  fix: string;
  drill_suggestion: string;
}

export interface TechniqueAnalysis {
  movement: string;
  timestamp_seconds: number;
  scores: TechniqueScores;
  strengths: string[];
  areas_for_improvement: ImprovementArea[];
}

export interface Highlight {
  timestamp_seconds: number;
  reason: string;
  type: 'great_form' | 'improvement' | 'notable_play';
  annotation_suggestion?: {
    type: string;
    description: string;
  };
}

export interface PriorityFocus {
  top_improvement: string;
  reasoning: string;
  expected_impact: string;
}

export interface VideoAnalysisResult {
  analysis_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  model_used: string;
  video_summary: VideoSummary | null;
  movements_detected: MovementDetected[];
  technique_analysis: TechniqueAnalysis[];
  highlights: Highlight[];
  priority_focus: PriorityFocus | null;
  recommendations: string[];
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  annotations_created: number;
  comment_id: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface AnalyzeVideoResponse {
  analysis_id: string;
  status: string;
  message: string;
  cost_usd: number;
  annotations_created: number;
  comment_created: boolean;
}

// Trigger AI analysis of a video (superadmin only)
export async function analyzeVideo(videoId: string): Promise<AnalyzeVideoResponse> {
  return apiRequest(`/api/v1/videos/${videoId}/analyze`, {
    method: 'POST',
  });
}

// Get analysis results for a video
export async function getVideoAnalysis(
  videoId: string
): Promise<{ analysis: VideoAnalysisResult | null }> {
  return apiRequest(`/api/v1/videos/${videoId}/analysis`);
}

// Clear AI-generated annotations (superadmin only)
export async function clearAnalysisAnnotations(
  videoId: string
): Promise<{ deleted_count: number; message: string }> {
  return apiRequest(`/api/v1/videos/${videoId}/analysis/annotations`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Admin API - SuperAdmin and Support operations
// ============================================================================

export interface PlatformStats {
  total_organizations: number;
  total_users: number;
  total_videos: number;
  retrieved_at: string;
}

export interface AdminOrg {
  org_id: string;
  name: string;
  owner_id: string;
  member_count: number;
  video_count: number;
  storage_bytes: number;
  storage_gb: number;
  estimated_monthly_cost: number;
  created_at: string;
}

export interface AdminOrgsResponse {
  organizations: AdminOrg[];
  total: number;
  platform_totals: {
    total_storage_gb: number;
    estimated_monthly_cost: number;
  };
}

export interface AdminOrgDetail extends AdminOrg {
  invite_code: string;
  members: Array<{
    user_id: string;
    role: string;
    joined_at: string;
    email?: string;
    given_name?: string;
    family_name?: string;
  }>;
}

export interface AdminUser {
  user_id: string;
  email: string;
  given_name: string;
  family_name: string;
  role: string;
  platform_role: string;
  org_count: number;
  created_at: string;
}

export interface AdminUserDetail extends AdminUser {
  bio?: string;
  phone_number?: string;
  memberships: Array<{
    org_id: string;
    org_name: string;
    role: string;
    joined_at: string;
  }>;
  video_count: number;
}

// Platform stats
export async function getAdminStats(): Promise<PlatformStats> {
  return apiRequest('/api/v1/admin/stats');
}

// Organizations
export async function adminListOrgs(): Promise<AdminOrgsResponse> {
  return apiRequest('/api/v1/admin/orgs');
}

export async function adminGetOrg(orgId: string): Promise<AdminOrgDetail> {
  return apiRequest(`/api/v1/admin/orgs/${orgId}`);
}

export async function adminDeleteOrg(
  orgId: string
): Promise<{ message: string; items_deleted: number }> {
  return apiRequest(`/api/v1/admin/orgs/${orgId}`, { method: 'DELETE' });
}

export async function adminMergeOrgs(
  sourceOrgId: string,
  targetOrgId: string
): Promise<{ message: string; merged: { members: number; videos: number; programs: number } }> {
  return apiRequest('/api/v1/admin/orgs/merge', {
    method: 'POST',
    body: JSON.stringify({ source_org_id: sourceOrgId, target_org_id: targetOrgId }),
  });
}

export async function adminCreateOrg(
  name: string,
  ownerId?: string
): Promise<{
  org: {
    org_id: string;
    name: string;
    owner_id: string;
    invite_code: string;
    created_at: string;
  };
  message: string;
}> {
  return apiRequest('/api/v1/admin/orgs', {
    method: 'POST',
    body: JSON.stringify({ name, owner_id: ownerId }),
  });
}

// Users
export async function adminListUsers(): Promise<{ users: AdminUser[]; total: number }> {
  return apiRequest('/api/v1/admin/users');
}

export interface CreateUserRequest {
  email: string;
  given_name?: string;
  family_name?: string;
  platform_role?: 'USER' | 'SUPPORT' | 'SUPER_ADMIN';
  org_id?: string;
  org_role?: 'OWNER' | 'COACH' | 'MODERATOR' | 'ATHLETE';
}

export interface CreateUserResponse {
  message: string;
  user: {
    user_id: string;
    email: string;
    given_name: string | null;
    family_name: string | null;
    platform_role: string;
  };
  membership?: {
    org_id: string;
    role: string;
  };
}

export async function adminCreateUser(data: CreateUserRequest): Promise<CreateUserResponse> {
  return apiRequest('/api/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminGetUser(userId: string): Promise<AdminUserDetail> {
  return apiRequest(`/api/v1/admin/users/${userId}`);
}

export async function adminUpdateUser(
  userId: string,
  data: {
    given_name?: string;
    family_name?: string;
    platform_role?: string;
    bio?: string;
    phone_number?: string;
  }
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteUser(
  userId: string
): Promise<{ message: string; items_deleted: number }> {
  return apiRequest(`/api/v1/admin/users/${userId}`, { method: 'DELETE' });
}

// Videos
export interface AdminVideo {
  video_id: string;
  user_id: string;
  org_id: string;
  title: string;
  status: string;
  file_size_bytes: number | null;
  created_at: string;
  owner_email?: string;
  owner_name?: string;
  org_name?: string;
}

export async function adminListVideos(): Promise<{ videos: AdminVideo[]; total: number }> {
  return apiRequest('/api/v1/admin/videos');
}

export async function adminDeleteVideo(videoId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/videos/${videoId}`, { method: 'DELETE' });
}

export async function adminAddUserMembership(
  userId: string,
  orgId: string,
  role: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/users/${userId}/memberships`, {
    method: 'POST',
    body: JSON.stringify({ org_id: orgId, role }),
  });
}

// Organization management
export async function adminUpdateOrg(
  orgId: string,
  data: { name?: string }
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/orgs/${orgId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateUserMembership(
  userId: string,
  orgId: string,
  role: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/users/${userId}/memberships/${orgId}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function adminRemoveUserMembership(
  userId: string,
  orgId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/admin/users/${userId}/memberships/${orgId}`, {
    method: 'DELETE',
  });
}

// CloudWatch Logs API
export interface LogGroup {
  name: string;
  short_name: string;
  stored_bytes: number;
  retention_days: number | null;
  created_at: string;
}

export interface LogEvent {
  timestamp: string;
  message: string;
  log_stream: string;
  event_id: string;
}

export async function getLogGroups(): Promise<{
  log_groups: LogGroup[];
  total: number;
  environment: string;
}> {
  return apiRequest('/api/v1/admin/logs/groups');
}

export async function getLogEvents(
  logGroup: string,
  options?: {
    limit?: number;
    filter?: string;
    start_time?: string;
    end_time?: string;
  }
): Promise<{
  events: LogEvent[];
  total: number;
  log_group: string;
  start_time: string;
  end_time: string;
  next_token?: string;
}> {
  const params = new URLSearchParams({ log_group: logGroup });
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.filter) params.set('filter', options.filter);
  if (options?.start_time) params.set('start_time', options.start_time);
  if (options?.end_time) params.set('end_time', options.end_time);
  return apiRequest(`/api/v1/admin/logs/events?${params.toString()}`);
}

// System Metrics API
export interface FunctionMetrics {
  invocations: number;
  errors: number;
  error_rate: number;
  avg_duration_ms: number;
  max_duration_ms: number;
}

export interface SystemMetrics {
  metrics: Record<string, FunctionMetrics>;
  summary: {
    total_invocations: number;
    total_errors: number;
    overall_error_rate: number;
  };
  period_seconds: number;
  time_range_hours: number;
  start_time: string;
  end_time: string;
}

export async function getSystemMetrics(hours?: number): Promise<SystemMetrics> {
  const params = hours ? `?hours=${hours}` : '';
  return apiRequest(`/api/v1/admin/metrics${params}`);
}

// CloudWatch Alarms API
export interface CloudWatchAlarm {
  alarm_name: string;
  description: string;
  state: 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';
  state_reason: string;
  state_updated_timestamp: string | null;
  metric_name: string;
  namespace: string;
  threshold: number;
  comparison_operator: string;
  evaluation_periods: number;
  period: number;
  severity: 'Critical' | 'Warning';
  resource: string;
}

export interface CloudWatchAlarmsResponse {
  alarms: CloudWatchAlarm[];
  summary: {
    total: number;
    in_alarm: number;
    ok: number;
    insufficient_data: number;
  };
  environment: string;
  retrieved_at: string;
}

export async function getCloudWatchAlarms(): Promise<CloudWatchAlarmsResponse> {
  return apiRequest('/api/v1/admin/alarms');
}

// Activity/Audit Log API
export interface AdminActivity {
  timestamp: string;
  admin_user_id: string;
  admin_email: string;
  action: string;
  details: Record<string, unknown>;
}

export async function getActivityLog(options?: {
  limit?: number;
  admin_id?: string;
}): Promise<{ activities: AdminActivity[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.admin_id) params.set('admin_id', options.admin_id);
  const query = params.toString();
  return apiRequest(`/api/v1/admin/activity${query ? `?${query}` : ''}`);
}

// AI Usage Stats
export interface AIUsageStats {
  period_days: number;
  summary: {
    total_analyses: number;
    completed: number;
    failed: number;
    success_rate: number;
    total_cost_usd: number;
    total_input_tokens: number;
    total_output_tokens: number;
    avg_cost_per_analysis: number;
  };
  by_organization: Array<{
    org_id: string;
    org_name: string;
    analysis_count: number;
    total_cost: number;
  }>;
  recent_analyses: Array<{
    analysis_id: string;
    video_id: string;
    org_id: string;
    status: string;
    cost_usd: number;
    input_tokens: number;
    output_tokens: number;
    created_at: string;
  }>;
}

export async function getAIUsageStats(days?: number): Promise<AIUsageStats> {
  const params = days ? `?days=${days}` : '';
  return apiRequest(`/api/v1/admin/ai-usage${params}`);
}

// ============================================================================
// Email Settings API - Platform email template management (Super Admin only)
// ============================================================================

export interface PlatformEmailSettings {
  // Brand identity
  platform_name: string;
  tagline: string;

  // Consent email
  consent_email_subject: string;
  consent_email_heading: string;
  consent_email_intro: string;
  consent_email_coppa_note: string;
  consent_email_approve_button: string;

  // Guardian promo
  guardian_promo_heading: string;
  guardian_promo_intro: string;
  guardian_promo_benefits: string[];
  guardian_promo_button: string;

  // About section
  about_heading: string;
  about_points: string[];

  // Approval email (to child)
  approval_email_subject: string;
  approval_email_heading: string;
  approval_email_body: string;
  approval_email_cta: string;
  approval_email_button: string;

  // Parent confirmation email
  parent_confirm_subject: string;
  parent_confirm_heading: string;
  parent_confirm_body: string;
  parent_confirm_promo_heading: string;
  parent_confirm_promo_intro: string;

  // Rejection email
  rejection_email_subject: string;
  rejection_email_body: string;
  rejection_email_cta: string;

  // Metadata
  updated_at: string | null;
  updated_by: string | null;
}

export interface EmailSettingsResponse {
  email_settings: PlatformEmailSettings;
  placeholders: {
    description: string;
    available: string[];
  };
}

export async function getPlatformEmailSettings(): Promise<EmailSettingsResponse> {
  return apiRequest('/api/v1/admin/email-settings');
}

export async function updatePlatformEmailSettings(
  updates: Partial<PlatformEmailSettings>
): Promise<{ message: string; updated_fields: string[] }> {
  return apiRequest('/api/v1/admin/email-settings', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// ============================================================================
// Billing API - Usage tracking and billing management
// ============================================================================

export interface BillingPlan {
  name: string;
  price_per_athlete: number;
  included_storage_gb_per_athlete: number;
  storage_overage_per_gb: number;
  max_athletes: number | null;
  max_programs: number | null;
  is_active: boolean;
  trial_ends_at: string | null;
  updated_at?: string;
}

export interface CurrentUsage {
  athlete_count: number;
  coach_count: number;
  video_count: number;
  reviewed_video_count: number;
  storage_bytes: number;
  storage_gb: number;
  data_transfer_gb: number;
  transcoding_minutes: number;
  annotation_count: number;
  program_count: number;
}

export interface EstimatedCost {
  athlete_count: number;
  base_charge: number;
  included_storage_gb: number;
  used_storage_gb: number;
  overage_gb: number;
  overage_charge: number;
  data_transfer_gb: number;
  data_transfer_charge: number;
  transcoding_minutes: number;
  transcoding_charge: number;
  total: number;
}

export interface BillingSummary {
  organization: {
    org_id: string;
    name: string;
  };
  plan: BillingPlan;
  current_usage: CurrentUsage;
  estimated_cost: EstimatedCost;
  billing_period: string;
  period_start: string;
  period_end: string;
}

export interface UsageSnapshot {
  date: string;
  athlete_count: number;
  coach_count: number;
  video_count: number;
  storage_gb: number;
  total_charge: number;
}

export interface UsageHistoryResponse {
  org_id: string;
  period: {
    start: string;
    end: string;
  };
  snapshots: UsageSnapshot[];
}

export interface Invoice {
  invoice_id: string;
  billing_period: string;
  athlete_count_avg: number;
  storage_gb_avg: number;
  base_charge: number;
  overage_charge: number;
  adjustments: number;
  total: number;
  status: 'PENDING' | 'DUE' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  due_date: string | null;
  paid_at: string | null;
}

export interface InvoicesResponse {
  org_id: string;
  invoices: Invoice[];
}

// Get billing summary for an organization
export async function getBillingSummary(orgId: string): Promise<BillingSummary> {
  return apiRequest(`/api/v1/orgs/${orgId}/billing`);
}

// Get usage history snapshots
export async function getUsageHistory(orgId: string, days?: number): Promise<UsageHistoryResponse> {
  const params = days ? `?days=${days}` : '';
  return apiRequest(`/api/v1/orgs/${orgId}/billing/usage${params}`);
}

// Get invoice history
export async function getInvoices(orgId: string, limit?: number): Promise<InvoicesResponse> {
  const params = limit ? `?limit=${limit}` : '';
  return apiRequest(`/api/v1/orgs/${orgId}/billing/invoices${params}`);
}

// Update billing plan pricing
export async function updateBillingPlan(
  orgId: string,
  data: {
    name?: string;
    price_per_athlete?: number;
    included_storage_gb_per_athlete?: number;
    storage_overage_per_gb?: number;
  }
): Promise<{ message: string; plan: BillingPlan }> {
  return apiRequest(`/api/v1/orgs/${orgId}/billing/plan`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// =============================================================================
// DIRECT BILLING & SUBSCRIPTIONS
// =============================================================================

export type SubscriptionTier =
  | 'FREE'
  | 'ATHLETE_BASIC'
  | 'ATHLETE_PRO'
  | 'COACH_BASIC'
  | 'COACH_PRO'
  | 'ENTERPRISE';

export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';

export type PaymentPlanInterval = 'MONTHLY' | 'QUARTERLY' | 'SEASONAL' | 'ANNUAL' | 'ONE_TIME';

export interface UserSubscription {
  has_subscription: boolean;
  subscription_id?: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  ai_credits_balance: number;
  ai_credits_monthly_allocation: number;
  credits_reset_at?: string | null;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface PortalSessionResponse {
  portal_url: string;
}

export interface CreditsBalance {
  balance: number;
  monthly_allocation: number;
  reset_at: string | null;
  recent_transactions: Array<{
    transaction_id: string;
    type: string;
    credits: number;
    balance_after: number;
    feature?: string;
    description?: string;
    created_at: string;
  }>;
}

export interface CreditsPackage {
  package_id: string;
  name: string;
  description?: string;
  credits: number;
  price_cents: number;
  is_popular: boolean;
}

export interface ConnectStatus {
  has_connect_account: boolean;
  account_id?: string;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  details_submitted?: boolean;
  onboarding_complete?: boolean;
}

export interface ConnectOnboardingResponse {
  onboarding_url: string;
  expires_at: number;
}

export interface ConnectDashboardResponse {
  dashboard_url: string;
}

export type PlanTargetAudience = 'GUARDIAN_ONLY' | 'ATHLETE_ONLY' | 'BOTH';

export interface OrgPaymentPlan {
  plan_id: string;
  name: string;
  description?: string;
  amount_cents: number;
  currency: string;
  interval: PaymentPlanInterval;
  interval_count: number;
  included_athletes: number;
  includes_ai_features: boolean;
  includes_highlights: boolean;
  ai_credits_per_period: number;
  family_discount_percent: number;
  target_audience: PlanTargetAudience;
  is_active: boolean;
}

export interface GuardianSubscription {
  has_subscription: boolean;
  subscription_id?: string;
  plan_id?: string;
  status?: SubscriptionStatus;
  athlete_ids?: string[];
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
}

// Tier pricing info from platform config
export interface TierPricing {
  name: string;
  description: string;
  price_cents: number;
  price_display: string;
  interval: string;
  features: string[];
  ai_credits_monthly: number;
  max_athletes?: number;
  popular?: boolean;
}

export interface PricingResponse {
  tiers: {
    ATHLETE_BASIC: TierPricing;
    ATHLETE_PRO: TierPricing;
    COACH_BASIC: TierPricing;
    COACH_PRO: TierPricing;
  };
  trial_days: number;
  currency: string;
}

// Get subscription pricing (public endpoint)
export async function getPricing(): Promise<PricingResponse> {
  return apiRequest('/api/v1/billing/pricing');
}

// Get current user's subscription
export async function getMySubscription(): Promise<UserSubscription> {
  return apiRequest('/api/v1/billing/subscription');
}

// Create Stripe checkout session for subscription
export async function createCheckoutSession(data: {
  tier: SubscriptionTier;
  success_url?: string;
  cancel_url?: string;
}): Promise<CheckoutSessionResponse> {
  return apiRequest('/api/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Create Stripe customer portal session
export async function createPortalSession(data?: {
  return_url?: string;
}): Promise<PortalSessionResponse> {
  return apiRequest('/api/v1/billing/portal', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Cancel user's subscription
export async function cancelSubscription(): Promise<{
  message: string;
  cancel_at: string | null;
}> {
  return apiRequest('/api/v1/billing/subscription/cancel', {
    method: 'POST',
  });
}

// Get AI credits balance and history
export async function getCreditsBalance(): Promise<CreditsBalance> {
  return apiRequest('/api/v1/billing/credits');
}

// Get available credits packages
export async function getCreditsPackages(): Promise<{
  packages: CreditsPackage[];
}> {
  return apiRequest('/api/v1/billing/credits/packages');
}

// Purchase AI credits package
export async function purchaseCredits(data: {
  package_id: string;
  success_url?: string;
  cancel_url?: string;
}): Promise<CheckoutSessionResponse> {
  return apiRequest('/api/v1/billing/credits/purchase', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Start Stripe Connect onboarding for org
export async function startConnectOnboarding(
  orgId: string,
  data?: { refresh_url?: string; return_url?: string }
): Promise<ConnectOnboardingResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/connect/onboard`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// Get org's Stripe Connect status
export async function getConnectStatus(orgId: string): Promise<ConnectStatus> {
  return apiRequest(`/api/v1/orgs/${orgId}/connect/status`);
}

// Get Stripe Express dashboard link
export async function getConnectDashboard(orgId: string): Promise<ConnectDashboardResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/connect/dashboard`);
}

// List org's payment plans
export async function listPaymentPlans(orgId: string): Promise<{ plans: OrgPaymentPlan[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/payment-plans`);
}

// Create payment plan for org
export async function createPaymentPlan(
  orgId: string,
  data: {
    name: string;
    amount_cents: number;
    description?: string;
    interval?: PaymentPlanInterval;
    interval_count?: number;
    included_athletes?: number;
    includes_ai_features?: boolean;
    includes_highlights?: boolean;
    ai_credits_per_period?: number;
    family_discount_percent?: number;
  }
): Promise<{ message: string; plan_id: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/payment-plans`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Update payment plan
export async function updatePaymentPlan(
  orgId: string,
  planId: string,
  data: Partial<Omit<OrgPaymentPlan, 'plan_id'>>
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/payment-plans/${planId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete (deactivate) payment plan
export async function deletePaymentPlan(
  orgId: string,
  planId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/payment-plans/${planId}`, {
    method: 'DELETE',
  });
}

// Subscribe guardian to org's payment plan
export async function guardianSubscribe(
  orgId: string,
  data: {
    plan_id: string;
    athlete_ids?: string[];
    success_url?: string;
    cancel_url?: string;
  }
): Promise<CheckoutSessionResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/guardian/subscribe`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Get guardian's subscription to org
export async function getGuardianSubscription(orgId: string): Promise<GuardianSubscription> {
  return apiRequest(`/api/v1/orgs/${orgId}/guardian/subscription`);
}

// Cancel guardian's subscription
export async function cancelGuardianSubscription(
  orgId: string
): Promise<{ message: string; cancel_at: string | null }> {
  return apiRequest(`/api/v1/orgs/${orgId}/guardian/subscription/cancel`, {
    method: 'POST',
  });
}

// Subscribe athlete directly to org's payment plan
export async function athleteSubscribe(
  orgId: string,
  data: {
    plan_id: string;
    success_url?: string;
    cancel_url?: string;
  }
): Promise<CheckoutSessionResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/athlete/subscribe`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Athlete subscription to an org's coach plan
export interface AthleteSubscription {
  subscription_id: string;
  athlete_id: string;
  org_id: string;
  payment_plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id: string;
  status: SubscriptionStatus;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
}

// Get athlete's direct subscription to org
export async function getAthleteDirectSubscription(
  orgId: string
): Promise<AthleteSubscription | null> {
  return apiRequest(`/api/v1/orgs/${orgId}/athlete/subscription`);
}

// Cancel athlete's direct subscription
export async function cancelAthleteDirectSubscription(
  orgId: string
): Promise<{ message: string; cancel_at: string | null }> {
  return apiRequest(`/api/v1/orgs/${orgId}/athlete/subscription/cancel`, {
    method: 'POST',
  });
}

// =============================================================================
// AI MODULE BUILDER
// =============================================================================

export interface AIGeneratedModule {
  title: string;
  description: string;
  duration_estimate: string;
  tags: string[];
  drills: Drill[];
  content: string;
  warmup?: {
    description: string;
    duration_seconds: number;
  };
  cooldown?: {
    description: string;
    duration_seconds: number;
  };
  equipment_needed?: string[];
  suggested_video_tags?: string[];
}

export interface AIGeneratedProgram {
  title: string;
  description: string;
  duration: string;
  modules: Array<{
    week: number;
    title: string;
    description: string;
    focus_areas: string[];
    duration_estimate: string;
    sessions_per_week: number;
    drills: Drill[];
    tags: string[];
  }>;
  progression_notes: string;
  equipment_needed: string[];
}

export interface MatchedVideo {
  video_id: string;
  title: string;
  tags: string[];
  match_score: number;
  matching_tags: string[];
  status: string;
  created_at: string;
}

export interface AIModuleSuggestion {
  focus_area: string;
  reason: string;
  suggested_drills: string[];
  priority: 'high' | 'medium' | 'low';
}

export interface GenerateModuleResponse {
  module: AIGeneratedModule;
  created_module?: {
    module_id: string;
    program_id: string;
    order: number;
    title: string;
    drills_count: number;
    matched_videos_count: number;
  };
  matched_videos: MatchedVideo[];
  generated_images: GeneratedImage[];
  generated_videos: GeneratedVideo[];
  ai_model: string;
}

export interface GenerateProgramResponse {
  program: AIGeneratedProgram;
  created_program?: {
    program_id: string;
    title: string;
    status: string;
    modules: Array<{
      module_id: string;
      order: number;
      title: string;
    }>;
  };
  ai_model: string;
}

export interface SuggestModulesResponse {
  suggestions: AIModuleSuggestion[];
  performance_summary?: Record<string, unknown>;
  message?: string;
  ai_model?: string;
}

// Chat message type for the AI chat interface
export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  data?: {
    type: 'module' | 'program' | 'suggestions' | 'videos';
    module?: AIGeneratedModule;
    program?: AIGeneratedProgram;
    suggestions?: AIModuleSuggestion[];
    matchedVideos?: MatchedVideo[];
    createdModule?: GenerateModuleResponse['created_module'];
    createdProgram?: GenerateProgramResponse['created_program'];
  };
}

// Generate a training module from natural language
export async function generateModule(
  orgId: string,
  prompt: string,
  options?: {
    programId?: string;
    generateImages?: boolean;
    generateVideos?: boolean;
    matchVideos?: boolean;
  }
): Promise<GenerateModuleResponse> {
  return apiRequest('/api/v1/ai/generate-module', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      prompt,
      program_id: options?.programId,
      generate_images: options?.generateImages ?? false,
      generate_videos: options?.generateVideos ?? false,
      match_videos: options?.matchVideos ?? true,
    }),
  });
}

// Generate a full training program with multiple modules
export async function generateProgram(
  orgId: string,
  prompt: string,
  createProgram: boolean = false
): Promise<GenerateProgramResponse> {
  return apiRequest('/api/v1/ai/generate-program', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      prompt,
      create_program: createProgram,
    }),
  });
}

// Get AI-powered module suggestions based on performance
export async function suggestModules(
  orgId: string,
  options?: {
    athleteId?: string;
    analysisType?: 'individual' | 'team';
  }
): Promise<SuggestModulesResponse> {
  return apiRequest('/api/v1/ai/suggest-modules', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      athlete_id: options?.athleteId,
      analysis_type: options?.analysisType ?? 'team',
    }),
  });
}

// Generate an instructional image for a drill
export async function generateDrillImage(
  prompt: string,
  drillName: string
): Promise<{ image: GeneratedImage }> {
  return apiRequest('/api/v1/ai/generate-image', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      drill_name: drillName,
    }),
  });
}

// Generate an instructional video clip for a drill
export async function generateDrillVideo(
  prompt: string,
  drillName: string
): Promise<{ video: GeneratedVideo }> {
  return apiRequest('/api/v1/ai/generate-video', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      drill_name: drillName,
    }),
  });
}

// Find videos from library that match given tags
export async function matchVideosByTags(
  orgId: string,
  tags: string[]
): Promise<{ matched_videos: MatchedVideo[]; search_tags: string[] }> {
  return apiRequest('/api/v1/ai/match-videos', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      tags,
    }),
  });
}

// =============================================================================
// MCP SERVER - AI ASSISTANT WITH TOOLS
// =============================================================================

export interface MCPTool {
  name: string;
  description: string;
  parameters: Record<
    string,
    {
      type: string;
      description: string;
      required?: boolean;
    }
  >;
}

export interface MCPToolsResponse {
  tools: Record<string, MCPTool>;
  user_role: string;
  platform_role: string;
  org_id: string;
}

export interface MCPToolResult {
  tool: string;
  result: Record<string, unknown>;
}

export interface MCPChatResponse {
  response: string;
  tool_calls: MCPToolResult[];
}

export interface MCPChatHistory {
  role: 'user' | 'assistant';
  content: string;
}

// =============================================================================
// ATHLETE ASSESSMENTS API - Coach skill evaluations
// =============================================================================

// List all assessments for an athlete
export async function listAthleteAssessments(
  orgId: string,
  athleteId: string,
  options?: { assessment_type?: string; limit?: number }
): Promise<AssessmentListResponse> {
  const params = new URLSearchParams();
  if (options?.assessment_type) params.set('assessment_type', options.assessment_type);
  if (options?.limit) params.set('limit', options.limit.toString());
  const queryString = params.toString();
  return apiRequest(
    `/api/v1/orgs/${orgId}/athletes/${athleteId}/assessments${queryString ? `?${queryString}` : ''}`
  );
}

// Create a new assessment for an athlete
export async function createAssessment(
  orgId: string,
  athleteId: string,
  data: CreateAssessmentRequest
): Promise<{ assessment: AthleteSkillAssessment; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/athletes/${athleteId}/assessments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Update an existing assessment
export async function updateAssessment(
  orgId: string,
  assessmentId: string,
  data: CreateAssessmentRequest
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/assessments/${assessmentId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete an assessment
export async function deleteAssessment(
  orgId: string,
  assessmentId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/assessments/${assessmentId}`, {
    method: 'DELETE',
  });
}

// Get available MCP tools for the current user
export async function getMCPTools(orgId: string): Promise<MCPToolsResponse> {
  return apiRequest(`/api/v1/mcp/tools?org_id=${encodeURIComponent(orgId)}`, {
    method: 'GET',
  });
}

// Execute a specific MCP tool
export async function executeMCPTool(
  orgId: string,
  toolName: string,
  parameters: Record<string, unknown>
): Promise<MCPToolResult> {
  return apiRequest('/api/v1/mcp/execute', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      tool: toolName,
      parameters,
    }),
  });
}

// Chat with AI assistant using MCP tools
export async function mcpChat(
  orgId: string,
  message: string,
  history?: MCPChatHistory[],
  context?: { video_id?: string | null }
): Promise<MCPChatResponse> {
  return apiRequest('/api/v1/mcp/chat', {
    method: 'POST',
    body: JSON.stringify({
      org_id: orgId,
      message,
      history: history || [],
      context: context || {},
    }),
  });
}

// =============================================================================
// FEED / ANNOUNCEMENTS API
// =============================================================================

export interface FeedPost {
  post_id: string;
  org_id: string;
  team_id?: string | null;
  author_id: string;
  author_name?: string;
  author_profile_picture?: string | null;
  post_type: 'ANNOUNCEMENT' | 'UPDATE' | 'ACHIEVEMENT' | 'REMINDER' | 'EVENT';
  title?: string | null;
  content: string;
  pinned: boolean;
  created_at: string;
  updated_at?: string | null;
  // Event-specific fields
  event_start?: string | null;
  event_end?: string | null;
  location?: string | null;
  reply_count?: number;
}

export interface FeedReply {
  reply_id: string;
  post_id: string;
  org_id: string;
  team_id?: string | null;
  author_id: string;
  author_name?: string;
  author_profile_picture?: string | null;
  content: string;
  created_at: string;
  updated_at?: string | null;
}

export async function listFeedPosts(
  orgId: string,
  options?: { team_id?: string; limit?: number }
): Promise<{ posts: FeedPost[] }> {
  const params = new URLSearchParams();
  if (options?.team_id) params.append('team_id', options.team_id);
  if (options?.limit) params.append('limit', options.limit.toString());
  const queryString = params.toString();
  return apiRequest(`/api/v1/orgs/${orgId}/feed${queryString ? '?' + queryString : ''}`);
}

export async function getFeedPost(orgId: string, postId: string): Promise<{ post: FeedPost }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}`);
}

export async function createFeedPost(
  orgId: string,
  data: {
    team_id?: string | null;
    post_type?: 'ANNOUNCEMENT' | 'UPDATE' | 'ACHIEVEMENT' | 'REMINDER' | 'EVENT';
    title?: string;
    content: string;
    pinned?: boolean;
    event_start?: string;
    event_end?: string;
    location?: string;
  }
): Promise<{ post: FeedPost }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFeedPost(
  orgId: string,
  postId: string,
  data: {
    title?: string;
    content?: string;
    pinned?: boolean;
    post_type?: 'ANNOUNCEMENT' | 'UPDATE' | 'ACHIEVEMENT' | 'REMINDER' | 'EVENT';
    event_start?: string;
    event_end?: string;
    location?: string;
  }
): Promise<{ post: FeedPost }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteFeedPost(orgId: string, postId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}`, {
    method: 'DELETE',
  });
}

// Feed Reply functions
export async function listFeedReplies(
  orgId: string,
  postId: string
): Promise<{ replies: FeedReply[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}/replies`);
}

export async function createFeedReply(
  orgId: string,
  postId: string,
  data: { content: string; athlete_id?: string }
): Promise<{ reply: FeedReply }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}/replies`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteFeedReply(
  orgId: string,
  postId: string,
  replyId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/feed/${postId}/replies/${replyId}`, {
    method: 'DELETE',
  });
}

// Get calendar file URL for an event
export function getEventCalendarUrl(orgId: string, postId: string): string {
  const config = getConfig();
  return `${config.api.endpoint}/api/v1/orgs/${orgId}/feed/${postId}/calendar`;
}

// =============================================================================
// FAMILY / GUARDIAN LINK API
// =============================================================================

export interface FamilyInvite {
  link_id: string;
  athlete_id: string;
  athlete_name: string;
  guardian_email: string;
  relationship: 'PARENT' | 'GUARDIAN' | 'FAMILY';
  invite_token?: string;
  expires_at?: string;
  created_at: string;
}

export interface FamilyLink {
  link_id: string;
  athlete_id: string;
  athlete_name: string;
  guardian_id?: string;
  guardian_name?: string;
  guardian_email?: string;
  relationship: 'PARENT' | 'GUARDIAN' | 'FAMILY';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  verified: boolean;
  created_at: string;
  // Guardian-managed flag - true for proxy accounts (minors without their own login)
  is_guardian_managed?: boolean;
  // Notification preferences
  notify_weekly_progress?: boolean;
  notify_monthly_progress?: boolean;
  notify_flags?: boolean;
}

export interface NotificationPreferences {
  notify_weekly_progress?: boolean;
  notify_monthly_progress?: boolean;
  notify_flags?: boolean;
}

export async function createFamilyInvite(
  orgId: string,
  data: {
    athlete_id: string;
    guardian_email: string;
    relationship?: 'PARENT' | 'GUARDIAN' | 'FAMILY';
  }
): Promise<{ invite: FamilyInvite; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/invites`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listFamilyInvites(orgId: string): Promise<{ invites: FamilyInvite[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/invites`);
}

export async function resendFamilyInvite(
  orgId: string,
  inviteId: string
): Promise<{ invite: FamilyInvite; message: string; email_sent: boolean }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/invites/${inviteId}/resend`, {
    method: 'POST',
  });
}

export async function listFamilyLinks(orgId: string): Promise<{ links: FamilyLink[] }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/links`);
}

export async function updateFamilyLink(
  orgId: string,
  linkId: string,
  data: { status: 'APPROVED' | 'REJECTED' }
): Promise<{ link: FamilyLink; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/links/${linkId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteFamilyLink(
  orgId: string,
  linkId: string
): Promise<{ message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/links/${linkId}`, {
    method: 'DELETE',
  });
}

export async function updateNotificationPreferences(
  orgId: string,
  linkId: string,
  preferences: NotificationPreferences
): Promise<{ link: { link_id: string } & NotificationPreferences; message: string }> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/links/${linkId}/notifications`, {
    method: 'PATCH',
    body: JSON.stringify(preferences),
  });
}

// =============================================================================
// HIGHLIGHTS & CLIPS API
// =============================================================================

export interface VideoClip {
  clip_id: string;
  video_id: string;
  user_id: string;
  org_id: string;
  start_time: number;
  end_time: number;
  duration: number;
  title?: string | null;
  description?: string | null;
  source: 'MANUAL' | 'AI_DETECTED' | 'AI_SUGGESTED';
  ai_confidence?: number | null;
  ai_category?: string | null;
  ai_description?: string | null;
  video_title?: string | null;
  athlete_name?: string | null;
  created_at: string;
}

export interface HighlightReel {
  reel_id: string;
  user_id: string;
  org_id: string;
  title: string;
  description?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'PROCESSING' | 'ARCHIVED';
  visibility: 'PRIVATE' | 'TEAM' | 'ORG' | 'PUBLIC';
  clips: ReelClip[];
  total_duration: number;
  clip_count: number;
  share_token?: string | null;
  share_url?: string | null;
  view_count: number;
  athlete_id?: string | null;
  athlete_name?: string | null;
  sport: string;
  position?: string | null;
  graduation_year?: number | null;
  ai_summary?: string | null;
  cover_clip_id?: string | null;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
}

export interface ReelClip {
  clip_id?: string | null;
  video_id: string;
  start_time: number;
  end_time: number;
  title?: string | null;
  video_title?: string | null;
  video_s3_key?: string | null;
  order: number;
}

// Create a clip from a video
export async function createClip(
  videoId: string,
  data: {
    start_time: number;
    end_time: number;
    title?: string;
    description?: string;
  }
): Promise<{ clip: VideoClip }> {
  return apiRequest(`/api/v1/videos/${videoId}/clips`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// List clips for a video
export async function listVideoClips(videoId: string): Promise<{ clips: VideoClip[] }> {
  return apiRequest(`/api/v1/videos/${videoId}/clips`);
}

// Delete a clip
export async function deleteClip(clipId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/clips/${clipId}`, {
    method: 'DELETE',
  });
}

// AI detect highlights in a video
export async function aiDetectHighlights(videoId: string): Promise<{
  message: string;
  clips_created: number;
  clips: VideoClip[];
  video_summary?: string;
}> {
  return apiRequest(`/api/v1/videos/${videoId}/clips/ai-detect`, {
    method: 'POST',
  });
}

// Create a highlight reel
export async function createHighlightReel(data: {
  title: string;
  org_id: string;
  description?: string;
  athlete_id?: string;
  visibility?: 'PRIVATE' | 'TEAM' | 'ORG' | 'PUBLIC';
  position?: string;
  graduation_year?: number;
}): Promise<{ reel: HighlightReel }> {
  return apiRequest('/api/v1/reels', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// List user's highlight reels
export async function listHighlightReels(orgId?: string): Promise<{ reels: HighlightReel[] }> {
  const url = orgId ? `/api/v1/reels?org_id=${orgId}` : '/api/v1/reels';
  return apiRequest(url);
}

// Get a highlight reel
export async function getHighlightReel(reelId: string): Promise<{ reel: HighlightReel }> {
  return apiRequest(`/api/v1/reels/${reelId}`);
}

// Update a highlight reel
export async function updateHighlightReel(
  reelId: string,
  data: Partial<{
    title: string;
    description: string;
    visibility: 'PRIVATE' | 'TEAM' | 'ORG' | 'PUBLIC';
    clips: ReelClip[];
    position: string;
    graduation_year: number;
    cover_clip_id: string;
  }>
): Promise<{ reel: HighlightReel }> {
  return apiRequest(`/api/v1/reels/${reelId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete a highlight reel
export async function deleteHighlightReel(reelId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/reels/${reelId}`, {
    method: 'DELETE',
  });
}

// Add clip to reel
export async function addClipToReel(
  reelId: string,
  data:
    | { clip_id: string }
    | { video_id: string; start_time: number; end_time?: number; title?: string }
): Promise<{ reel: HighlightReel; clip_added: ReelClip }> {
  return apiRequest(`/api/v1/reels/${reelId}/clips`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Remove clip from reel
export async function removeClipFromReel(
  reelId: string,
  clipId: string
): Promise<{ reel: HighlightReel }> {
  return apiRequest(`/api/v1/reels/${reelId}/clips/${clipId}`, {
    method: 'DELETE',
  });
}

// Publish reel (generate share link)
export async function publishHighlightReel(
  reelId: string
): Promise<{ reel: HighlightReel; share_url: string; share_token: string }> {
  return apiRequest(`/api/v1/reels/${reelId}/publish`, {
    method: 'POST',
  });
}

// Get public reel by share token (no auth required)
export async function getPublicReel(shareToken: string): Promise<{ reel: HighlightReel }> {
  return apiRequest(`/api/v1/reels/share/${shareToken}`);
}

// Generate AI summary for reel
export async function generateReelAISummary(
  reelId: string
): Promise<{ reel: HighlightReel; ai_summary: string }> {
  return apiRequest(`/api/v1/reels/${reelId}/ai-summary`, {
    method: 'POST',
  });
}

// =============================================================================
// PLAY TAGS & PLAY LIBRARY API
// =============================================================================

export type PlayTagCategory =
  | 'SHOT_TYPE'
  | 'BALL_HANDLING'
  | 'PASSING'
  | 'SCREENING'
  | 'CUTTING'
  | 'DEFENSE'
  | 'REBOUNDING'
  | 'TRANSITION'
  | 'SET_PLAY'
  | 'RESULT'
  | 'HIGHLIGHT'
  | 'CUSTOM';

export interface PlayTag {
  tag_id: string;
  video_id: string;
  user_id: string;
  org_id: string;
  category: PlayTagCategory;
  tag_name: string;
  display_name: string;
  timestamp_seconds: number;
  duration_seconds: number;
  source: 'MANUAL' | 'AI_AUTO' | 'AI_SUGGESTED';
  ai_confidence?: number | null;
  description?: string | null;
  result?: string | null;
  athlete_id?: string | null;
  athlete_name?: string | null;
  court_x?: number | null;
  court_y?: number | null;
  clip_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlayTagPreset {
  tag_name: string;
  display_name: string;
}

export interface PlayTagStats {
  total_tags: number;
  by_category: Record<string, number>;
  by_tag_name: Record<string, number>;
  by_result: Record<string, number>;
  by_source: Record<string, number>;
  recent_tags: PlayTag[];
  shot_chart: Record<
    string,
    {
      made: number;
      missed: number;
      total: number;
      percentage: number;
    }
  >;
}

// Create a play tag
export async function createPlayTag(
  videoId: string,
  data: {
    category: PlayTagCategory;
    tag_name: string;
    timestamp_seconds: number;
    duration_seconds?: number;
    description?: string;
    result?: string;
  }
): Promise<{ tag: PlayTag }> {
  return apiRequest(`/api/v1/videos/${videoId}/play-tags`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// List play tags for a video
export async function listVideoPlayTags(
  videoId: string,
  category?: PlayTagCategory
): Promise<{ tags: PlayTag[]; count: number }> {
  const url = category
    ? `/api/v1/videos/${videoId}/play-tags?category=${category}`
    : `/api/v1/videos/${videoId}/play-tags`;
  return apiRequest(url);
}

// Update a play tag
export async function updatePlayTag(
  tagId: string,
  data: Partial<{
    description: string;
    result: string;
    tag_name: string;
    category: PlayTagCategory;
  }>
): Promise<{ tag: PlayTag }> {
  return apiRequest(`/api/v1/play-tags/${tagId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Delete a play tag
export async function deletePlayTag(tagId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/play-tags/${tagId}`, {
    method: 'DELETE',
  });
}

// AI detect and tag plays in a video
export async function aiDetectPlays(
  videoId: string,
  createClips = false
): Promise<{
  message: string;
  tags_created: number;
  tags: PlayTag[];
  clips_created: number;
  video_summary?: string;
}> {
  return apiRequest(`/api/v1/videos/${videoId}/play-tags/ai-detect`, {
    method: 'POST',
    body: JSON.stringify({ create_clips: createClips }),
  });
}

// Get play tag presets
export async function getPlayTagPresets(): Promise<{
  presets: Record<string, PlayTagPreset[]>;
  categories: Array<{
    value: string;
    name: string;
    tags: PlayTagPreset[];
  }>;
}> {
  return apiRequest('/api/v1/play-library/presets');
}

// Search play library
export async function searchPlayLibrary(params: {
  org_id: string;
  category?: PlayTagCategory;
  tag_name?: string;
  athlete_id?: string;
  result?: string;
  limit?: number;
}): Promise<{ tags: PlayTag[]; count: number }> {
  const searchParams = new URLSearchParams();
  searchParams.set('org_id', params.org_id);
  if (params.category) searchParams.set('category', params.category);
  if (params.tag_name) searchParams.set('tag_name', params.tag_name);
  if (params.athlete_id) searchParams.set('athlete_id', params.athlete_id);
  if (params.result) searchParams.set('result', params.result);
  if (params.limit) searchParams.set('limit', params.limit.toString());

  return apiRequest(`/api/v1/play-library?${searchParams.toString()}`);
}

// Get play library stats
export async function getPlayLibraryStats(
  orgId: string,
  athleteId?: string
): Promise<{ stats: PlayTagStats }> {
  const url = athleteId
    ? `/api/v1/play-library/stats?org_id=${orgId}&athlete_id=${athleteId}`
    : `/api/v1/play-library/stats?org_id=${orgId}`;
  return apiRequest(url);
}

// =============================================================================
// COPPA COMPLIANCE - Data Deletion & Export
// =============================================================================

export interface AthleteDataDeleteResponse {
  message: string;
  athlete_name: string;
  deleted_counts: {
    videos: number;
    comments: number;
    annotations: number;
    assessments: number;
    workout_logs: number;
    program_assignments: number;
    family_links: number;
    parental_consent: number;
    s3_objects: number;
  };
  completed_at: string;
}

export interface AthleteDataExport {
  export_info: {
    requested_by: string;
    requested_at: string;
    athlete_id: string;
    org_id: string;
    export_type: string;
  };
  profile: {
    user_id: string;
    email: string;
    given_name: string;
    family_name: string;
    date_of_birth?: string;
    position?: string;
    jersey_number?: string;
    height?: string;
    weight?: string;
    bio?: string;
    created_at: string;
    updated_at: string;
  };
  videos: Array<{
    video_id: string;
    title: string;
    description?: string;
    status: string;
    file_size_bytes?: number;
    created_at: string;
    updated_at: string;
    tags: string[];
    download_url?: string;
    comments: Array<{
      comment_id: string;
      author_id: string;
      author_name: string;
      content: string;
      timestamp_seconds?: number;
      created_at: string;
    }>;
    annotations: Array<{
      annotation_id: string;
      author_id: string;
      timestamp_seconds: number;
      annotation_data: unknown;
      created_at: string;
    }>;
  }>;
  assessments: Array<{
    assessment_id: string;
    assessment_type: string;
    ratings: Record<string, number>;
    notes?: string;
    assessed_by: string;
    created_at: string;
  }>;
  workout_logs: Array<{
    log_id: string;
    program_id: string;
    module_id?: string;
    drill_name: string;
    sets_completed?: number;
    reps_completed?: number;
    weight_used?: string;
    duration_seconds?: number;
    notes?: string;
    completed_at: string;
  }>;
  program_assignments: Array<{
    assignment_id: string;
    program_id: string;
    program_title: string;
    assigned_by: string;
    assigned_at: string;
    status: string;
    progress: Record<string, unknown>;
  }>;
  family_links: Array<{
    link_id: string;
    guardian_id?: string;
    guardian_name?: string;
    relationship: string;
    status: string;
    created_at: string;
  }>;
  parental_consent: Array<{
    request_id: string;
    parent_email: string;
    status: string;
    requested_at: string;
    responded_at?: string;
  }>;
}

/**
 * COPPA Compliance: Request deletion of all data for a linked athlete.
 * Only guardians with APPROVED links can request this.
 * This action is PERMANENT and cannot be undone.
 */
export async function requestAthleteDataDeletion(
  orgId: string,
  athleteId: string
): Promise<AthleteDataDeleteResponse> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/athlete/${athleteId}/delete-request`, {
    method: 'POST',
  });
}

/**
 * COPPA Compliance: Export all data collected about a linked athlete.
 * Only guardians with APPROVED links can request this.
 * Returns JSON containing all athlete data including video download URLs.
 */
export async function exportAthleteData(
  orgId: string,
  athleteId: string
): Promise<AthleteDataExport> {
  return apiRequest(`/api/v1/orgs/${orgId}/family/athlete/${athleteId}/export`);
}

// =============================================================================
// CAMPAIGNS API - Marketing campaigns and announcements
// =============================================================================

export type CampaignType = 'modal' | 'banner' | 'wizard' | 'toast';
export type CampaignAudience =
  | 'all'
  | 'new_users'
  | 'athletes'
  | 'coaches'
  | 'free_tier'
  | 'paid'
  | 'inactive'
  | 'custom';
export type CampaignStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
export type CampaignTrigger =
  | 'login'
  | 'first_visit'
  | 'page_view'
  | 'after_action'
  | 'scheduled'
  | 'manual';

export interface Campaign {
  campaign_id: string;
  name: string;
  campaign_type: CampaignType;
  title: string;
  subtitle?: string | null;
  body?: string | null;
  image_url?: string | null;
  video_url?: string | null;
  cta_text?: string | null;
  cta_url?: string | null;
  cta_style: string;
  secondary_cta_text?: string | null;
  wizard_steps: Array<Record<string, unknown>>;
  audience: CampaignAudience;
  status: CampaignStatus;
  trigger: CampaignTrigger;
  trigger_page?: string | null;
  priority: number;
  show_once: boolean;
  frequency_cap?: number | null;
  delay_seconds: number;
  dismissible: boolean;
  theme: string;
  background_color?: string | null;
  accent_color?: string | null;
  view_count: number;
  click_count: number;
  dismiss_count: number;
  conversion_count: number;
  linked_challenge_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Get active campaigns for the current user.
 * Campaigns are filtered by trigger type and audience targeting.
 */
export async function getActiveCampaigns(
  trigger: CampaignTrigger = 'login',
  page?: string,
  limit?: number
): Promise<{ campaigns: Campaign[] }> {
  const params = new URLSearchParams({ trigger });
  if (page) params.append('page', page);
  if (limit) params.append('limit', limit.toString());
  return apiRequest(`/api/v1/campaigns/active?${params}`);
}

/**
 * Record that the user viewed a campaign.
 */
export async function recordCampaignView(campaignId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/campaigns/${campaignId}/view`, {
    method: 'POST',
  });
}

/**
 * Record that the user clicked a campaign CTA.
 */
export async function recordCampaignClick(campaignId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/campaigns/${campaignId}/click`, {
    method: 'POST',
  });
}

/**
 * Record that the user dismissed a campaign.
 */
export async function recordCampaignDismiss(campaignId: string): Promise<{ message: string }> {
  return apiRequest(`/api/v1/campaigns/${campaignId}/dismiss`, {
    method: 'POST',
  });
}
